import React, { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
// ROLE_TECH_MAP and ROLE_CATEGORIES handled inside Phase3RoleTechSelector
import Phase3RoleTechSelector from '../components/Phase3RoleTechSelector';
import { prefillCandidate } from '../utils/schedulePrefill';
import Phase3QuestionSourcesPreview from '../components/Phase3QuestionSourcesPreview';
import AdminHome from '../components/AdminHome';
import { Sidebar, TopBar } from '../components/AppShell';
import { LEGACY_MEETINGS } from '../config/features';
import { useJobTechScreen } from '../utils/techScreen';

function CompanyAdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [activeMenu, setActiveMenu] = useState('dashboard');

  const [wizardStep, setWizardStep] = useState(1);
  const [searchFilter, setSearchFilter] = useState('');
  const [showPlansModal, setShowPlansModal] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [expandedUsers, setExpandedUsers] = useState({});
  
  // Upload states (Essential)
  const [jdFile, setJdFile] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [audioFormatError, setAudioFormatError] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [candidateHistory, setCandidateHistory] = useState(null);
  const [, setHistoryChecking] = useState(false);
  // Essential — selected hiring manager
  const [essentialHmId, setEssentialHmId] = useState('');
  const [essentialHmName, setEssentialHmName] = useState('');
  // Essential — async polling
  const [pollingRequirementId, setPollingRequirementId] = useState(null);
  const [pollingStatus, setPollingStatus] = useState('');

  // Schedule Meeting states (Professional)
  const [hiringManagers, setHiringManagers] = useState([]);
  const [selectedHiringManager, setSelectedHiringManager] = useState('');
  const [meetingTitle, setMeetingTitle] = useState('');
  const [meetingDate, setMeetingDate] = useState('');
  const [meetingTime, setMeetingTime] = useState('');
  const [selectedHmTimezone, setSelectedHmTimezone] = useState('');
  const [duration, setDuration] = useState(30);
  const [meetingJdFile, setMeetingJdFile] = useState(null);
  const [isLoadingManagers, setIsLoadingManagers] = useState(false);
  const [isScheduling, setIsScheduling] = useState(false);
  const [scheduleError, setScheduleError] = useState('');
  const [scheduleSuccess, setScheduleSuccess] = useState('');

  // Duplicate-JD warning modal (shared by Phase 1 upload + Phase 2 schedule)
  const [duplicateWarning, setDuplicateWarning] = useState({ show: false, duplicates: [], phase: null });

  // JD Data grouped by user
  const [jdByUser, setJdByUser] = useState([]);
  const [isLoadingJDs, setIsLoadingJDs] = useState(true);

  // Phase 3 states (Enterprise)
const [selectedRequirement, setSelectedRequirement] = useState('');
const [selectedRequirementTitle, setSelectedRequirementTitle] = useState('');
const [sourcesPreview, setSourcesPreview] = useState(null);   // JD/intake sources for the selected requirement
const [reqSearch, setReqSearch] = useState('');
const [reqDropdownOpen, setReqDropdownOpen] = useState(false);
const [candidateName, setCandidateName] = useState('');
const [candidateEmail, setCandidateEmail] = useState('');
const [hmName, setHmName] = useState('');
const [hmEmail, setHmEmail] = useState('');
const [roleType, setRoleType] = useState('');
const [selectedTechStacks, setSelectedTechStacks] = useState([]);
const [interviewDate, setInterviewDate] = useState('');
const [interviewTime, setInterviewTime] = useState('');
const [candidateTimezone, setCandidateTimezone] = useState(
  Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York'
);
const [interviewDuration, setInterviewDuration] = useState(60);
const [codingMode, setCodingMode] = useState('verbal_pseudocode');
const [techScreenFromJob, applyJobTechScreen, clearJobTechScreen] = useJobTechScreen(setInterviewDuration, setCodingMode);   // HM's technical screen (field 18)
const [proctoringStrictness, setProctoringStrictness] = useState('medium');
const [resume, setResume] = useState(null);
// isLoadingPhase3Reqs removed — loading state handled inside Phase3InterviewTracker
const [isSchedulingPhase3, setIsSchedulingPhase3] = useState(false);
const [phase3Error, setPhase3Error] = useState('');
const [phase3Success, setPhase3Success] = useState('');

  // ── Company AI meetings (scheduled_meetings) ───────────────────────────
  const [companyMeetings, setCompanyMeetings] = useState([]);
  const [isLoadingMeetings, setIsLoadingMeetings] = useState(false);
const [expandedRequirements, setExpandedRequirements] = useState({});
  const [rescheduleItem, setRescheduleItem] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [rescheduleLoading, setRescheduleLoading] = useState(false);

  // ── My Interviews (company-wide) ────────────────────────────────────
  const [allInterviews, setAllInterviews] = useState([]);
  const [groupPages, setGroupPages] = useState({});
  const [meetGroupPages, setMeetGroupPages] = useState({});
  const [isLoadingInterviews, setIsLoadingInterviews] = useState(false);
  const [interviewsSearch, setInterviewsSearch] = useState('');
  const [interviewsFilter, setInterviewsFilter] = useState('all');
  const [dashPeriod, setDashPeriod] = useState('month');
  const [expandedInterviewUsers, setExpandedInterviewUsers] = useState({});
  const [editingInterview, setEditingInterview] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');
  const [editSuccess, setEditSuccess] = useState('');

  useEffect(() => {
    const userData = localStorage.getItem('user');
    const companyData = localStorage.getItem('company');
    if (userData) setUser(JSON.parse(userData));
    if (companyData) setCompany(JSON.parse(companyData));
  }, []);


  // Fetch requirements — defined at component level so delete can refresh
  const fetchJDsByUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/'); return; }
    try {
      setIsLoadingJDs(true);
      const tokenPayload = JSON.parse(atob(token.split('.')[1]));
      const companyId = tokenPayload.company_id;
      if (!companyId) return;
      const response = await fetch(API_ENDPOINTS.REQUIREMENTS_COMPANY_GROUPED(companyId), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const result = await response.json();
        setJdByUser(result.data || []);
      } else if (response.status === 401) { localStorage.clear(); navigate('/'); }
    } catch (error) { console.error('Error fetching requirements:', error); }
    finally { setIsLoadingJDs(false); }
  };

  useEffect(() => {
    fetchJDsByUser();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // Load interviews on mount for dashboard activity card
  useEffect(() => {
    fetchAllInterviews();
    fetchCompanyMeetings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch hiring managers when Professional tab is opened
  const fetchHiringManagers = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      setIsLoadingManagers(true);
      const response = await fetch(API_ENDPOINTS.MEETINGS_HIRING_MANAGERS, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const result = await response.json();
        setHiringManagers(result.data || []);
      }
    } catch (error) { console.error('Error fetching hiring managers:', error); }
    finally { setIsLoadingManagers(false); }
  };


  // Fetch all company AI meetings (for My Meetings page)
  const fetchCompanyMeetings = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setIsLoadingMeetings(true);
    try {
      const BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const res = await fetch(`${BASE}/api/meetings/company-meetings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCompanyMeetings(data.data || []);
      }
    } catch (e) { console.error('fetchCompanyMeetings:', e); }
    finally { setIsLoadingMeetings(false); }
  };

  // Fetch all company interviews
  const openEditInterview = (iv) => {
    let dateVal = '', timeVal = '';
    if (iv.scheduled_datetime) {
      const dt = new Date(iv.scheduled_datetime);
      dateVal = dt.toISOString().split('T')[0];
      timeVal = dt.toTimeString().slice(0, 5);
    }
    setEditForm({
      candidate_name:        iv.candidate_name || '',
      candidate_email:       iv.candidate_email || '',
      hiring_manager_name:   iv.hiring_manager_name || '',
      hiring_manager_email:  iv.hiring_manager_email || '',
      interview_level:       iv.interview_level || 'intermediate',
      role_type:             iv.role_type || '',
      tech_stack:            iv.tech_stack || '',
      coding_mode:           iv.coding_mode || 'verbal_pseudocode',
      proctoring_strictness: iv.proctoring_strictness || 'medium',
      duration_minutes:      iv.duration_minutes || 60,
      scheduled_date:        dateVal,
      scheduled_time:        timeVal,
    });
    setEditError('');
    setEditSuccess('');
    setEditingInterview(iv);
  };

  const handleSaveEditInterview = async () => {
    if (!editingInterview) return;
    setEditLoading(true);
    setEditError('');
    setEditSuccess('');
    try {
      const tok = localStorage.getItem('token');
      const BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      let scheduled_datetime = null;
      if (editForm.scheduled_date && editForm.scheduled_time)
        scheduled_datetime = `${editForm.scheduled_date}T${editForm.scheduled_time}:00`;
      const payload = {
        candidate_name:        editForm.candidate_name || null,
        candidate_email:       editForm.candidate_email || null,
        hiring_manager_name:   editForm.hiring_manager_name || null,
        hiring_manager_email:  editForm.hiring_manager_email || null,
        interview_level:       editForm.interview_level || null,
        role_type:             editForm.role_type || null,
        tech_stack:            editForm.tech_stack || null,
        coding_mode:           editForm.coding_mode || null,
        proctoring_strictness: editForm.proctoring_strictness || null,
        duration_minutes:      editForm.duration_minutes ? parseInt(editForm.duration_minutes) : null,
        scheduled_datetime,
      };
      const res = await fetch(`${BASE}/api/phase3/interview/${editingInterview.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${tok}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await res.json();
      if (res.ok) {
        setEditSuccess('Interview updated successfully!');
        fetchAllInterviews();
        setTimeout(() => { setEditingInterview(null); setEditSuccess(''); }, 1500);
      } else {
        setEditError(result.detail || 'Update failed.');
      }
    } catch (e) {
      setEditError('Network error. Please try again.');
    } finally {
      setEditLoading(false);
    }
  };

  const fetchAllInterviews = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setIsLoadingInterviews(true);
    try {
      const BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const res = await fetch(`${BASE}/api/phase3/company-interviews`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAllInterviews(data.data || data.interviews || []);
      } else if (res.status === 401) { localStorage.clear(); navigate('/'); }
    } catch (e) { console.error('Error fetching interviews:', e); }
    finally { setIsLoadingInterviews(false); }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('company');
    navigate('/');
  };

  // ── Idle timeout: 30 min inactivity → auto-logout ──────────────────────────
  // Warning shown at 25 min, auto-logout at 30 min.
  const IDLE_TIMEOUT_MS = 30 * 60 * 1000;
  const IDLE_WARNING_MS = 25 * 60 * 1000;
  const [idleWarning, setIdleWarning] = useState(false);
  const idleTimerRef   = useRef(null);
  const idleWarnRef    = useRef(null);

  useEffect(() => {
    const resetTimer = () => {
      setIdleWarning(false);
      clearTimeout(idleWarnRef.current);
      clearTimeout(idleTimerRef.current);
      idleWarnRef.current  = setTimeout(() => setIdleWarning(true), IDLE_WARNING_MS);
      idleTimerRef.current = setTimeout(() => {
        localStorage.removeItem('token'); localStorage.removeItem('user'); localStorage.removeItem('company');
        navigate('/');
      }, IDLE_TIMEOUT_MS);
    };
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    events.forEach(e => window.addEventListener(e, resetTimer));
    resetTimer();
    return () => {
      events.forEach(e => window.removeEventListener(e, resetTimer));
      clearTimeout(idleWarnRef.current);
      clearTimeout(idleTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch live subscription data (phase usage, billing period, status)
  useEffect(() => {
    const tok = localStorage.getItem('token');
    if (!tok) return;
    const BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    fetch(`${BASE}/api/stripe/subscription`, {
      headers: { 'Authorization': `Bearer ${tok}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setSubscriptionData(data); })
      .catch(() => {});
  }, []);

  // Plan access
  const planHierarchy = { essential: 1, professional: 2, enterprise: 3 };
  const subscriptionPlan = company?.subscription_plan?.toLowerCase() || 'essential';
  const monthlyLimit = company?.monthly_limit || 20;

  const isPlanAccessible = (plan) => {
    return (planHierarchy[subscriptionPlan] || 0) >= (planHierarchy[plan] || 0);
  };

  const exportToExcel = (rows, filename) => {
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Report');
    XLSX.writeFile(wb, `${filename}_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  const exportToPDF = (columns, rows, title, filename) => {
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFontSize(13);
    doc.setTextColor(30, 64, 175);
    doc.text(title, 14, 15);
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text(`Generated ${new Date().toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'})}  |  Quali5d AI Recruitment Intelligence`, 14, 21);
    autoTable(doc, {
      startY: 26,
      head: [columns],
      body: rows,
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: [30, 64, 175], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 250, 252] },
    });
    doc.save(`${filename}_${new Date().toISOString().slice(0,10)}.pdf`);
  };

  const handleMenuClick = (menu) => {
    if (menu === 'essential' || menu === 'professional' || menu === 'enterprise') {
      if (!isPlanAccessible(menu)) {
        setShowPlansModal(true);
        return;
      }
      if ((menu === 'essential' || menu === 'professional' || menu === 'my-interviews') && hiringManagers.length === 0) {
        fetchHiringManagers();
      }
    }
    if (menu === 'my-meetings') fetchCompanyMeetings();
    setActiveMenu(menu);
  };

  // Deep links from the shared sidebar / profile menu: ?menu=enterprise | my-interviews | subscription | my-jobs.
  // Handled once, only after the company (its plan) has loaded — so the plans modal never opens by mistake — and
  // then removed from the URL, so Back or returning here never re-triggers it.
  const deepLinkDone = useRef(false);
  useEffect(() => {
    if (!company || deepLinkDone.current) return;
    deepLinkDone.current = true;
    const menu = new URLSearchParams(window.location.search).get('menu');
    if (!menu) return;
    if (menu === 'my-jobs') { navigate('/jobs', { replace: true }); return; }
    const qs = new URLSearchParams(window.location.search);
    if (menu === 'enterprise' && qs.get('job') && qs.get('candidate')) schedulePrefill.current = { job: qs.get('job'), candidate: qs.get('candidate') };
    navigate('/dashboard/company-admin', { replace: true });
    if (menu === 'my-interviews') { setActiveMenu('my-interviews'); fetchAllInterviews(); }
    else if (menu === 'enterprise') handleMenuClick('enterprise');
    else if (menu === 'subscription') setShowPlansModal(true);
  }, [company]); // eslint-disable-line react-hooks/exhaustive-deps

  // Choosing a job in the schedule wizard — from the dropdown, or pre-filled from a candidate's application.
  const pickRequirement = (jd) => {
    const reqId = String(jd.id);
    setSelectedRequirement(reqId);
    setSelectedRequirementTitle(jd.jobTitle || '');
    applyJobTechScreen(jd.tech_screen);
    setReqSearch('');
    setReqDropdownOpen(false);
    let foundHmName = jd.hiringManagerName || jd.hiring_manager_name || '';
    let foundHmEmail = jd.hiringManagerEmail || jd.hiring_manager_email || '';
    if (foundHmName && !foundHmEmail && hiringManagers.length > 0) {
      const matchedHm = hiringManagers.find(hm =>
        hm.full_name?.toLowerCase() === foundHmName.toLowerCase() ||
        hm.full_name?.toLowerCase().includes(foundHmName.toLowerCase())
      );
      if (matchedHm) foundHmEmail = matchedHm.email || '';
    }
    setHmName(foundHmName);
    setHmEmail(foundHmEmail);
    setRoleType('');
    setSelectedTechStacks([]);
  };

  // "Schedule Alex screen" on a submitted candidate (candidate pipeline, bundle 4a-2):
  // ?menu=enterprise&job=<id>&candidate=<id> → once the job list has loaded, the job + hiring manager are picked as if
  // chosen from the list, and the candidate's name, e-mail and resume are filled in. Everything stays editable.
  const schedulePrefill = useRef(null);
  useEffect(() => {
    const p = schedulePrefill.current;
    if (!p || activeMenu !== 'enterprise') return;
    const jd = (jdByUser || []).flatMap((g) => (g.jds || []).filter((j) => j.status === 'completed'))
      .find((j) => String(j.id) === String(p.job));
    if (!jd) return;
    schedulePrefill.current = null;
    pickRequirement(jd);
    prefillCandidate(p.candidate, p.job, { setCandidateName, setCandidateEmail, setResume, setPhase3Error });
  }, [activeMenu, jdByUser]); // eslint-disable-line react-hooks/exhaustive-deps

  const SIDEBAR_KEY = { dashboard: 'dashboard', enterprise: 'screen', 'my-interviews': 'interviews' };
  const onSidebar = (key) => {
    if (key === 'dashboard') handleMenuClick('dashboard');
    else if (key === 'screen') navigate('/technical-screen');          // 4f: the one-step Alex screen (the long form stays, unlinked)
    else if (key === 'interviews') { setActiveMenu('my-interviews'); fetchAllInterviews(); }
    else navigate(key === 'jobs' ? '/jobs' : '/organization');
  };

  const toggleUserExpand = (userId) => {
    setExpandedUsers(prev => ({ ...prev, [userId]: !prev[userId] }));
  };

  // Essential - File upload handlers
  const handleJdFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const fileName = file.name.toLowerCase();
      const validExtensions = ['.pdf', '.doc', '.docx', '.txt'];
      if (!validExtensions.some(ext => fileName.endsWith(ext))) {
        setUploadError('Invalid file type. Please upload PDF, DOC, DOCX, or TXT');
        setJdFile(null); return;
      }
      setJdFile(file); setUploadError('');
    }
  };

  const handleAudioFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const name = file.name.toLowerCase();
      const supported = ['.mp3', '.wav', '.webm', '.ogg', '.flac'];
      const unsupported = ['.wma', '.wmv', '.aiff', '.aif', '.opus', '.amr', '.m4a', '.mp4', '.mov', '.avi', '.aac'];
      const fmt = name.split('.').pop().toUpperCase();
      if (unsupported.some(ext => name.endsWith(ext))) {
        setAudioFormatError({ filename: file.name, format: fmt, type: 'unsupported' });
        setAudioFile(null);
        e.target.value = '';
        return;
      }
      if (!supported.some(ext => name.endsWith(ext))) {
        setAudioFormatError({ filename: file.name, format: fmt, type: 'unrecognized' });
        setAudioFile(null);
        e.target.value = '';
        return;
      }
      setAudioFile(file);
      setUploadError('');
    }
  };

  const handleCandidateHistoryCheck = async () => {
    if (!candidateEmail.trim() || !candidateName.trim()) return;
    const BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    const token = localStorage.getItem('token');
    setHistoryChecking(true);
    try {
      const res = await fetch(
        `${BASE}/api/phase3/candidate-history?email=${encodeURIComponent(candidateEmail.trim())}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const json = await res.json();
      if (res.ok && json.found) {
        setCandidateHistory(json.data);
        setShowHistoryModal(true);
        return true;
      } else {
        setCandidateHistory([]);
        return false;
      }
    } catch(e) {
      console.error('Candidate history check failed:', e);
      return false;
    } finally {
      setHistoryChecking(false);
    }
  };

  const handleProcessFiles = async (forceProceed = false) => {
    if (!essentialHmId) { setUploadError('Please select a Hiring Manager first'); return; }
    if (!jdFile) { setUploadError('Please upload a Job Description file'); return; }
    if (!audioFile) { setUploadError('Please upload an Intake Recording'); return; }
    setIsProcessing(true); setUploadError(''); setUploadSuccess(false);
    setPollingRequirementId(null); setPollingStatus('');
    try {
      const formData = new FormData();
      formData.append('jd_file', jdFile);
      if (audioFile) formData.append('audio_video_file', audioFile);
      formData.append('hiring_manager_id', essentialHmId);
      formData.append('hiring_manager_name', essentialHmName);
      if (forceProceed) formData.append('force_proceed', 'true');
      const token = localStorage.getItem('token');
      const response = await fetch(API_ENDPOINTS.REQUIREMENTS_PROCESS, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData
      });
      const result = await response.json();
      if (response.status === 401) { localStorage.clear(); navigate('/'); return; }
      if (!response.ok) { setUploadError(result.detail || 'Failed to process files.'); setIsProcessing(false); return; }

      // Duplicate JD detected — surface prior results, let the user decide
      if (result.status === 'duplicate_warning') {
        setDuplicateWarning({ show: true, duplicates: result.duplicates || [], phase: 'phase1' });
        setIsProcessing(false); return;
      }

      // Duplicate — results already available, navigate immediately
      if (result.is_duplicate) {
        navigate('/requirement-results', { state: { resultData: { ...result.data, is_duplicate: true } } });
        setIsProcessing(false); return;
      }

      // Sync fallback — backend processed synchronously (Redis unavailable)
      if (result.status === 'success' && result.data && result.data.recruiter_document) {
        navigate('/requirement-results', { state: { resultData: { ...result.data, is_duplicate: false } } });
        setIsProcessing(false); return;
      }

      // Async path — task queued in Redis, start polling
      const requirementId = result.requirement_id || (result.data && result.data.requirement_id);
      if (!requirementId) { setUploadError('Processing started but could not get requirement ID.'); setIsProcessing(false); return; }

      setPollingRequirementId(requirementId);
      setPollingStatus('processing');
      setIsProcessing(false);

      const pollInterval = setInterval(async () => {
        try {
          const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
          const statusRes = await fetch(`${API_BASE}/api/requirements/${requirementId}/status`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (!statusRes.ok) return;
          const statusData = await statusRes.json();
          const processingStatus = statusData.data && statusData.data.processing_status;
          setPollingStatus(processingStatus);

          if (processingStatus === 'completed') {
            clearInterval(pollInterval);
            setPollingRequirementId(null); setPollingStatus('');
            navigate('/requirement-results', { state: { resultData: { ...statusData.data, is_duplicate: false } } });
          }
          if (processingStatus === 'failed') {
            clearInterval(pollInterval);
            setPollingRequirementId(null); setPollingStatus('');
            setUploadError((statusData.data && statusData.data.error) || 'Processing failed. Please try again.');
          }
        } catch (pollErr) { console.error('Polling error:', pollErr); }
      }, 5000);

    } catch (error) {
      setUploadError('Connection error. Please check your connection and try again.');
      setIsProcessing(false);
    }
  };

  // Professional - Schedule Meeting handlers
  const handleMeetingJdFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const fileName = file.name.toLowerCase();
      const validExtensions = ['.pdf', '.doc', '.docx', '.txt'];
      if (!validExtensions.some(ext => fileName.endsWith(ext))) {
        setScheduleError('Invalid file type. Please upload PDF, DOC, DOCX, or TXT');
        setMeetingJdFile(null); return;
      }
      setMeetingJdFile(file); setScheduleError('');
    }
  };

  const handleScheduleMeeting = async (forceProceed = false) => {
    setScheduleError(''); setScheduleSuccess('');
    if (!selectedHiringManager || !meetingTitle || !meetingDate || !meetingTime) {
      setScheduleError('Please fill in all required fields');
      return;
    }
    if (!meetingJdFile) {
      setScheduleError('Please upload a Job Description file');
      return;
    }
    setIsScheduling(true);
    try {
      const token = localStorage.getItem('token');

      // Build query params (backend expects these as URL params)
      const params = new URLSearchParams({
        hiring_manager_id: selectedHiringManager,
        meeting_title: meetingTitle,
        scheduled_date: meetingDate,
        scheduled_time: meetingTime,
        duration_minutes: duration,
        hm_timezone: selectedHmTimezone || 'America/New_York'
      });
      if (forceProceed) params.append('force_proceed', 'true');

      // Only JD file goes as FormData body
      const formData = new FormData();
      formData.append('jd_file', meetingJdFile);

      const response = await fetch(API_ENDPOINTS.MEETINGS_SCHEDULE + '?' + params.toString(), {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData
      });
      const result = await response.json();

      // Duplicate JD detected — surface prior results, let the user decide
      if (result.status === 'duplicate_warning') {
        setDuplicateWarning({ show: true, duplicates: result.duplicates || [], phase: 'phase2' });
        setIsScheduling(false); return;
      }

      if (response.ok) {
        setScheduleSuccess('Meeting scheduled successfully! Notification sent to hiring manager.');
        setSelectedHiringManager(''); setMeetingTitle(''); setMeetingDate('');
        setMeetingTime(''); setDuration(30); setMeetingJdFile(null); setSelectedHmTimezone('');
        // Go back to dashboard after 2 seconds
        setTimeout(() => { setActiveMenu('dashboard'); setScheduleSuccess(''); }, 2000);
      } else {
        // Handle error - detail can be string or array of objects
        const errorMsg = typeof result.detail === 'string' ? result.detail
          : Array.isArray(result.detail) ? result.detail.map(e => e.msg).join(', ')
          : 'Failed to schedule meeting';
        setScheduleError(errorMsg);
      }
    } catch (error) { setScheduleError('Connection error. Please try again.'); }
    finally { setIsScheduling(false); }
  };

  // FIXED handleSchedulePhase3 function
// Replace the old handleSchedulePhase3 in all 3 dashboards with this

const handleSchedulePhase3 = async () => {
  // Validation
  if (!selectedRequirement) { setPhase3Error('Please select a requirement'); return; }
  if (!candidateName.trim()) { setPhase3Error('Please enter candidate name'); return; }
  if (!candidateEmail.trim()) { setPhase3Error('Please enter candidate email'); return; }
  if (!interviewDate) { setPhase3Error('Please select interview date'); return; }
  if (!interviewTime) { setPhase3Error('Please select interview time'); return; }
  if (!candidateTimezone) { setPhase3Error('Please select candidate timezone'); return; }
  if (!roleType) { setPhase3Error('Please select role type'); return; }
  if (selectedTechStacks.length === 0) { setPhase3Error('Please select at least one tech stack'); return; }
  if (!resume) { setPhase3Error('Please upload candidate resume (PDF or DOCX)'); return; }
  
  // Validate email
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(candidateEmail)) { setPhase3Error('Please enter a valid email address'); return; }

  setIsSchedulingPhase3(true);
  setPhase3Error('');
  setPhase3Success('');

  try {
    const formData = new FormData();
    const token = localStorage.getItem('token');
    const tokenPayload = JSON.parse(atob(token.split('.')[1]));
    
    formData.append('company_id', tokenPayload.company_id);
    formData.append('requirement_id', selectedRequirement);
    formData.append('candidate_email', candidateEmail);
    formData.append('candidate_name', candidateName);
    formData.append('role_type', roleType);
    formData.append('tech_stack', selectedTechStacks.join(', '));
    formData.append('scheduled_datetime', `${interviewDate}T${interviewTime}`);
    formData.append('candidate_timezone', candidateTimezone);
    formData.append('duration_minutes', interviewDuration);
    formData.append('coding_mode', codingMode);
    formData.append('proctoring_strictness', proctoringStrictness);
    if (hmName.trim()) formData.append('hiring_manager_name', hmName.trim());
    if (hmEmail.trim()) formData.append('hiring_manager_email', hmEmail.trim());
    formData.append('resume', resume);

    const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/phase3/schedule-interview`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    });

    const result = await response.json();

    if (response.ok) {
      setPhase3Success(result.message || 'Interview scheduled successfully!');
      setSelectedRequirement(''); clearJobTechScreen();
      setSelectedRequirementTitle('');
      setCandidateName('');
      setCandidateEmail('');
      setHmName('');
      setHmEmail('');
      setRoleType('');
      setSelectedTechStacks([]);
      setWizardStep(1);
      setInterviewDate('');
      setInterviewTime('');
      setCandidateTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York');
      setInterviewDuration(60);
      setCodingMode('verbal_pseudocode');
      setProctoringStrictness('medium');
      setResume(null);
      
      const fileInput = document.getElementById('phase3-resume');
      if (fileInput) fileInput.value = '';
      
      setTimeout(() => { setActiveMenu('dashboard'); setPhase3Success(''); }, 2000);
    } else {
      // FIX: Handle Pydantic validation errors properly
      let errorMsg = 'Failed to schedule interview';
      
      if (result.detail) {
        // If detail is an array (Pydantic validation errors)
        if (Array.isArray(result.detail)) {
          errorMsg = result.detail.map(err => err.msg).join(', ');
        } 
        // If detail is a string
        else if (typeof result.detail === 'string') {
          errorMsg = result.detail;
        }
      }
      
      setPhase3Error(errorMsg);
    }
  } catch (error) {
    console.error('Schedule error:', error);
    setPhase3Error('Connection error. Please try again.');
  } finally {
    setIsSchedulingPhase3(false);
  }
};

  const totalUsers = jdByUser.length;
  const totalJDUsed = jdByUser.reduce((sum, u) => sum + u.totalJDs, 0);
  const today = new Date().toISOString().split('T')[0];

  // ── Subscription spend (addon spend wired up post-payment-gateway) ──
  const planMonthlyPrice = subscriptionPlan === 'enterprise' ? 499 : subscriptionPlan === 'professional' ? 199 : 99;
  const subscriptionStartDate = company?.created_at ? new Date(company.created_at) : null;
  const monthsActive = subscriptionStartDate
    ? Math.max(1, Math.round((Date.now() - subscriptionStartDate.getTime()) / (1000 * 60 * 60 * 24 * 30)))
    : 1;
  const estimatedSpend = planMonthlyPrice * monthsActive;

  // ── Requirements usage ──────────────────────────────────────────────
  const requirementsUsed = totalJDUsed;
  const requirementsLeft = Math.max(0, monthlyLimit - requirementsUsed);

  // ── Interview counts (all users) ────────────────────────────────────
  const totalInterviews = allInterviews.length;
  const interviewsCompleted = allInterviews.filter(i => (i.status || '').toLowerCase() === 'completed').length;
  const interviewsScheduled = allInterviews.filter(i => (i.status || '').toLowerCase() === 'scheduled').length;

  // ── Group meetings by user (self first as "Me (Admin)") ─────────────
  const currentUserId = user?.id;
  const groupByUser = (items, idKey, nameKey) => {
    const map = {};
    items.forEach(item => {
      const uid = item[idKey];
      const uname = item[nameKey] || 'Unknown';
      if (!map[uid]) map[uid] = { userId: uid, userName: uname, items: [] };
      map[uid].items.push(item);
    });
    const groups = Object.values(map);
    // Put current admin first labeled "Me (Admin)"
    groups.forEach(g => { if (g.userId === currentUserId) g.userName = 'Me (Admin)'; });
    groups.sort((a, b) => {
      if (a.userId === currentUserId) return -1;
      if (b.userId === currentUserId) return 1;
      return a.userName.localeCompare(b.userName);
    });
    return groups;
  };

  // meetingGroups replaced by jdByUser (no separate meetings API)
  const normalizedInterviews = allInterviews.map(i => ({
    ...i,
    _groupUserId: i.recruiter_id || currentUserId,
    _groupUserName: i.recruiter_name || i.recruiter_email || 'Unknown',
  }));
  const interviewGroups = groupByUser(normalizedInterviews, '_groupUserId', '_groupUserName');

  // ── Meeting/interview filter helpers ────────────────────────────────

  const filterInterviews = (items) => {
    let out = items;
    if (interviewsFilter !== 'all') out = out.filter(i => (i.status||'').toLowerCase() === interviewsFilter);
    if (interviewsSearch.trim()) {
      const q = interviewsSearch.toLowerCase();
      out = out.filter(i =>
        i.candidate_name?.toLowerCase().includes(q) ||
        i.role_type?.toLowerCase().includes(q) ||
        i.hiring_manager_name?.toLowerCase().includes(q)
      );
    }
    return out;
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'processing': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'failed': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };
  const getStatusIcon = (status) => {
    switch(status) {
      case 'completed': return '✅'; case 'processing': return '⏳';
      case 'failed': return '❌'; default: return '⏸️';
    }
  };

  // Plans data for modal
  const plansData = [
    {
      key: 'essential', name: 'Essential', icon: '📦', price: 99, limit: '10 JD uploads/month',
      gradient: 'from-green-500 to-emerald-600', lightGradient: 'from-green-50 to-emerald-50',
      borderColor: 'border-green-200', textColor: 'text-green-700',
      features: ['Upload & process job descriptions', 'AI-powered requirement extraction', 'Auto-generated screening questions', 'Quality score & missing field alerts', 'Recruiter-friendly document output', 'Email support']
    },
    {
      key: 'professional', name: 'Professional', icon: '💼', price: 199, limit: '5 JD uploads + 10 intake calls/month',
      gradient: 'from-brand-blue to-blue-600', lightGradient: 'from-blue-50 to-indigo-50',
      borderColor: 'border-blue-200', textColor: 'text-blue-700',
      features: ['Everything in Essential', 'AI conducts intake calls with hiring managers', 'Automated requirement gathering from calls', 'Meeting scheduling & management', 'Priority email + chat support', 'Advanced analytics dashboard', 'Team collaboration tools']
    },
    {
      key: 'enterprise', name: 'Enterprise', icon: '🚀', price: 499, limit: '5 JD uploads + 10 calls + 10 interviews/month',
      gradient: 'from-purple-500 to-indigo-600', lightGradient: 'from-purple-50 to-indigo-50',
      borderColor: 'border-purple-200', textColor: 'text-purple-700',
      features: ['Everything in Professional', 'AI-powered technical interviews', 'Candidate scoring & ranking', 'Dedicated account manager', 'Phone + priority support', 'Custom ATS integrations', 'SLA guarantee', 'Advanced compliance reporting']
    }
  ];

  // Work in progress renderer (kept for future use)
  // eslint-disable-next-line no-unused-vars
  const renderWorkInProgress = (planName) => (
    <div className="text-center p-20">
      <div className="inline-block bg-gradient-to-br from-yellow-100 to-orange-100 rounded-full p-8 mb-6 shadow-lg">
        <div className="text-8xl">🚧</div>
      </div>
      <h2 className="text-3xl font-bold text-gray-900 mb-3">{planName} Plan</h2>
      <p className="text-xl text-gray-600 font-medium">Work in Progress</p>
      <p className="text-gray-500 mt-2">This feature is under development</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex">
      {/* ── Idle timeout warning modal ── */}
      {idleWarning && (
        <div className="fixed inset-0 bg-black/60 z-[9999] flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full text-center">
            <div className="text-5xl mb-4">⏰</div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Still there?</h2>
            <p className="text-gray-500 text-sm mb-6">You'll be automatically logged out in <span className="font-bold text-red-600">5 minutes</span> due to inactivity.</p>
            <button
              onClick={() => { setIdleWarning(false); }}
              className="w-full py-3 bg-purple-600 text-white font-bold rounded-xl hover:bg-purple-700 transition-all">
              Yes, keep me logged in
            </button>
            <button
              onClick={handleLogout}
              className="w-full py-2 mt-2 text-sm text-gray-400 hover:text-gray-600">
              Log out now
            </button>
          </div>
        </div>
      )}
      <Sidebar active={SIDEBAR_KEY[activeMenu]} onSelect={onSidebar} footer={(
        <div className="px-4 pb-6">
          <div className={`bg-gradient-to-br ${
            subscriptionPlan === 'enterprise' ? 'from-purple-50 to-indigo-50 border-purple-200' :
            subscriptionPlan === 'professional' ? 'from-blue-50 to-indigo-50 border-blue-200' :
            'from-green-50 to-emerald-50 border-green-200'
          } rounded-2xl p-5 border`}>
            <div className="flex items-center gap-3 mb-3">
              <span className="text-2xl">
                {subscriptionPlan === 'enterprise' ? '🚀' : subscriptionPlan === 'professional' ? '💼' : '📦'}
              </span>
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Current Plan</p>
                <p className={`text-lg font-bold capitalize ${
                  subscriptionPlan === 'enterprise' ? 'text-purple-700' :
                  subscriptionPlan === 'professional' ? 'text-blue-700' : 'text-green-700'
                }`}>{subscriptionPlan}</p>
              </div>
            </div>
            <div className="flex items-baseline gap-1 mb-2">
              <span className="text-2xl font-bold text-gray-900">
                ${subscriptionPlan === 'enterprise' ? 499 : subscriptionPlan === 'professional' ? 199 : 99}
              </span>
              <span className="text-sm text-gray-500">/month</span>
            </div>

            {/* Billing status */}
            {subscriptionData && (
              <div className="mb-3">
                {subscriptionData.status === 'cancelling' && (
                  <p className="text-xs text-amber-600 font-medium bg-amber-50 rounded-lg px-2 py-1">
                    ⚠️ Cancels {subscriptionData.current_period_end ? new Date(subscriptionData.current_period_end).toLocaleDateString() : 'end of period'}
                  </p>
                )}
                {subscriptionData.status === 'past_due' && (
                  <p className="text-xs text-red-600 font-medium bg-red-50 rounded-lg px-2 py-1">
                    ⚠️ Payment past due
                  </p>
                )}
                {subscriptionData.current_period_end && subscriptionData.status === 'active' && (
                  <p className="text-xs text-gray-400">
                    Renews {new Date(subscriptionData.current_period_end).toLocaleDateString()}
                  </p>
                )}
              </div>
            )}

            {/* Phase usage bars */}
            {subscriptionData && (
              <div className="space-y-2 mb-3">
                <div>
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>📄 JD Uploads</span>
                    <span>{subscriptionData.phase1_used}/{subscriptionData.phase1_limit}</span>
                  </div>
                  <div className="bg-gray-200 rounded-full h-1.5">
                    <div className="bg-blue-500 h-1.5 rounded-full transition-all"
                      style={{ width: `${subscriptionData.phase1_limit > 0 ? Math.min(100, (subscriptionData.phase1_used / subscriptionData.phase1_limit) * 100) : 0}%` }}/>
                  </div>
                </div>
                {subscriptionData.phase2_limit > 0 && (
                  <div>
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>🤝 Intake Calls</span>
                      <span>{subscriptionData.phase2_used}/{subscriptionData.phase2_limit}</span>
                    </div>
                    <div className="bg-gray-200 rounded-full h-1.5">
                      <div className="bg-green-500 h-1.5 rounded-full transition-all"
                        style={{ width: `${Math.min(100, (subscriptionData.phase2_used / subscriptionData.phase2_limit) * 100)}%` }}/>
                    </div>
                  </div>
                )}
                {subscriptionData.phase3_limit > 0 && (
                  <div>
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>🎯 Interviews</span>
                      <span>{subscriptionData.phase3_used}/{subscriptionData.phase3_limit}</span>
                    </div>
                    <div className="bg-gray-200 rounded-full h-1.5">
                      <div className="bg-purple-500 h-1.5 rounded-full transition-all"
                        style={{ width: `${Math.min(100, (subscriptionData.phase3_used / subscriptionData.phase3_limit) * 100)}%` }}/>
                    </div>
                  </div>
                )}
              </div>
            )}

            <button
              onClick={() => setShowPlansModal(true)}
              className={`w-full py-2.5 rounded-xl text-sm font-bold text-white transition-all hover:shadow-lg transform hover:scale-105 bg-gradient-to-r ${
                subscriptionPlan === 'enterprise' ? 'from-purple-500 to-indigo-600' :
                subscriptionPlan === 'professional' ? 'from-brand-blue to-blue-600' :
                'from-green-500 to-emerald-600'
              }`}
            >
              Manage Subscription
            </button>
          </div>
        </div>
      )} />

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <TopBar onSubscription={() => setShowPlansModal(true)} />

        <div className="p-8">
          {/* ========== DASHBOARD ========== */}
          {activeMenu === 'dashboard' && (
            <div className="space-y-5">
              {/* The admin dashboard (Organization bundle 2); approvals / assigning and HM jobs live under Jobs */}
              <AdminHome onOpenJobs={() => navigate('/jobs')} />

              {/* ── Gradient Hero Card + Period Toggle ── */}
              {(()=>{
                const now = new Date();
                const periodStart = (p) => {
                  const d = new Date(now);
                  if (p==='week')    { d.setDate(d.getDate()-7); return d; }
                  if (p==='month')   { d.setMonth(d.getMonth()-1); return d; }
                  if (p==='quarter') { d.setMonth(d.getMonth()-3); return d; }
                  return new Date(0);
                };
                const prevPeriodStart = (p) => {
                  const d = new Date(now);
                  if (p==='week')    { d.setDate(d.getDate()-14); return d; }
                  if (p==='month')   { d.setMonth(d.getMonth()-2); return d; }
                  if (p==='quarter') { d.setMonth(d.getMonth()-6); return d; }
                  return new Date(0);
                };
                const curStart  = periodStart(dashPeriod);
                const prevStart = prevPeriodStart(dashPeriod);
                const prevEnd   = periodStart(dashPeriod);
                const inCur  = (d) => { const dt=new Date(d); return dt>=curStart && dt<=now; };
                const inPrev = (d) => { const dt=new Date(d); return dt>=prevStart && dt<prevEnd; };

                // All requirements (flatten jdByUser)
                const allJds = jdByUser.flatMap(u => u.jds || []);

                const curJobs  = dashPeriod==='all' ? allJds.length : allJds.filter(j=>inCur(j.date||j.created_at)).length;
                const curIvs   = dashPeriod==='all' ? allInterviews.length : allInterviews.filter(iv=>inCur(iv.scheduled_datetime||iv.created_at)).length;
                const curSel   = dashPeriod==='all'
                  ? allInterviews.filter(iv=>iv.hm_decision==='move_forward').length
                  : allInterviews.filter(iv=>iv.hm_decision==='move_forward'&&inCur(iv.scheduled_datetime||iv.created_at)).length;
                const prevJobs = allJds.filter(j=>inPrev(j.date||j.created_at)).length;
                const prevIvs  = allInterviews.filter(iv=>inPrev(iv.scheduled_datetime||iv.created_at)).length;
                const prevSel  = allInterviews.filter(iv=>iv.hm_decision==='move_forward'&&inPrev(iv.scheduled_datetime||iv.created_at)).length;

                const trend = (cur, prev) => {
                  if (dashPeriod==='all'||prev===0) return null;
                  const p = Math.round(((cur-prev)/prev)*100);
                  return { pct:p, up:p>=0 };
                };
                const selRate     = curIvs>0 ? Math.round(curSel/curIvs*100) : 0;
                const prevSelRate = prevIvs>0 ? Math.round(prevSel/prevIvs*100) : 0;
                const selRateTrend = dashPeriod!=='all'&&prevIvs>0 ? { pct:selRate-prevSelRate, up:selRate>=prevSelRate } : null;

                return (
                  <div className="bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-900 rounded-2xl p-6 text-white shadow-xl relative overflow-hidden">
                    <div className="absolute inset-0 opacity-10" style={{backgroundImage:'radial-gradient(circle at 20% 80%, #60a5fa 0%, transparent 50%), radial-gradient(circle at 80% 20%, #818cf8 0%, transparent 50%)'}}></div>
                    <div className="relative">
                      <div className="flex items-start justify-between mb-6">
                        <div>
                          <h1 className="text-2xl font-black">{company?.company_name || 'Company'} Overview</h1>
                          <p className="text-blue-200 text-sm mt-0.5">{new Date().toLocaleDateString('en-US',{weekday:'long',month:'long',day:'numeric',year:'numeric'})}</p>
                        </div>
                        <div className="flex items-center gap-1 bg-white/10 rounded-xl p-1">
                          {['week','month','quarter','all'].map(p=>(
                            <button key={p} onClick={()=>setDashPeriod(p)}
                              className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${dashPeriod===p?'bg-white text-slate-900 shadow':'text-white/70 hover:text-white hover:bg-white/10'}`}>
                              {p==='week'?'Week':p==='month'?'Month':p==='quarter'?'Quarter':'All'}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        {[
                          { label:'Jobs Created',   val:curJobs,      t:trend(curJobs,prevJobs), sub:`${allJds.length} total` },
                          { label:'Interviews',     val:curIvs,       t:trend(curIvs,prevIvs),   sub:`${allInterviews.length} total` },
                          { label:'Selection Rate', val:`${selRate}%`, t:selRateTrend,            sub:`${curSel} selected` },
                        ].map(k=>(
                          <div key={k.label} className="bg-white/10 hover:bg-white/15 rounded-xl p-4 cursor-pointer transition-all">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-bold text-blue-200 uppercase tracking-widest">{k.label}</span>
                              {k.t && (
                                <span className={`flex items-center gap-0.5 text-xs font-bold px-2 py-0.5 rounded-full ${k.t.up?'bg-green-500/20 text-green-300':'bg-red-500/20 text-red-300'}`}>
                                  {k.t.up?'↑':'↓'}{Math.abs(k.t.pct)}%
                                </span>
                              )}
                            </div>
                            <div className="text-4xl font-black text-white mb-1">{k.val}</div>
                            <div className="text-xs text-blue-200">{k.sub}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* ── Subscription & Usage (unchanged) ── */}
              <div>
                <h2 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Subscription & Usage</h2>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {/* Current Plan */}
                  <div className={`bg-gradient-to-br ${
                    subscriptionPlan === 'enterprise' ? 'from-purple-50 to-indigo-100 border-purple-200' :
                    subscriptionPlan === 'professional' ? 'from-blue-50 to-blue-100 border-blue-200' :
                    'from-green-50 to-emerald-100 border-green-200'
                  } rounded-2xl p-5 border shadow-sm hover:shadow-md transition-shadow`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-xs font-bold uppercase tracking-wide ${
                        subscriptionPlan === 'enterprise' ? 'text-purple-600' :
                        subscriptionPlan === 'professional' ? 'text-blue-600' : 'text-green-600'
                      }`}>Current Plan</span>
                      <span className="text-xl">{subscriptionPlan === 'enterprise' ? '🚀' : subscriptionPlan === 'professional' ? '💼' : '📦'}</span>
                    </div>
                    <div className={`text-3xl font-black capitalize mb-1 ${
                      subscriptionPlan === 'enterprise' ? 'text-purple-900' :
                      subscriptionPlan === 'professional' ? 'text-blue-900' : 'text-green-900'
                    }`}>{subscriptionPlan}</div>
                    <div className="text-sm text-gray-500 font-medium">${planMonthlyPrice}/month</div>
                  </div>
                  {/* Requirements Used */}
                  <div className="bg-gradient-to-br from-amber-50 to-orange-100 rounded-2xl p-5 border border-amber-200 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold uppercase tracking-wide text-amber-600">Requirements Used</span>
                      <span className="text-xl">📊</span>
                    </div>
                    <div className="text-3xl font-black text-amber-900 mb-1">{requirementsUsed}<span className="text-lg text-amber-500 font-semibold"> / {monthlyLimit}</span></div>
                    <div className="w-full bg-amber-200 rounded-full h-1.5 mt-2">
                      <div className="bg-amber-500 h-1.5 rounded-full transition-all" style={{ width: `${Math.min(100, (requirementsUsed / monthlyLimit) * 100)}%` }}></div>
                    </div>
                    <div className="text-xs text-amber-600 mt-1 font-medium">{requirementsLeft} remaining this month</div>
                  </div>
                  {/* Total Users */}
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-5 border border-blue-200 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold uppercase tracking-wide text-blue-600">Team Members</span>
                      <span className="text-xl">👥</span>
                    </div>
                    <div className="text-3xl font-black text-blue-900 mb-1">{totalUsers}</div>
                    <div className="text-sm text-blue-600 font-medium">Active users</div>
                  </div>
                  {/* Estimated Spend */}
                  <div className="bg-gradient-to-br from-slate-50 to-gray-100 rounded-2xl p-5 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold uppercase tracking-wide text-gray-500">Est. Spend</span>
                      <span className="text-xl">💰</span>
                    </div>
                    <div className="text-3xl font-black text-gray-900 mb-1">${estimatedSpend.toLocaleString()}</div>
                    <div className="text-xs text-gray-500 font-medium">Subscription · {monthsActive} mo</div>
                    <div className="text-[10px] text-gray-400 mt-1 italic">Add-on spend: coming soon</div>
                  </div>
                </div>
              </div>

              {/* ── Pending Actions Banner (unchanged) ── */}
              {(()=>{
                const items = [];
                const processingCount = jdByUser.reduce((s,u)=>s+u.jds.filter(j=>j.status==='processing').length,0);
                if (processingCount > 0)
                  items.push({ icon:'⏳', text:`${processingCount} role brief${processingCount>1?'s':''} still processing`, pill:'Processing', pillColor:'bg-yellow-100 text-yellow-700 border-yellow-200', action:null });
                const reportsReady = companyMeetings.filter(m=>m.status==='completed'&&m.requirement_id).length;
                if (LEGACY_MEETINGS && reportsReady > 0)
                  items.push({ icon:'📋', text:`${reportsReady} intake call report${reportsReady>1?'s':''} ready to review`, pill:'View Meetings →', pillColor:'bg-blue-100 text-blue-700 border-blue-200', action:()=>setActiveMenu('my-meetings') });
                if (interviewsScheduled > 0)
                  items.push({ icon:'🎙️', text:`${interviewsScheduled} technical screen${interviewsScheduled>1?'s':''} scheduled`, pill:'View Interviews →', pillColor:'bg-purple-100 text-purple-700 border-purple-200', action:()=>{ setActiveMenu('my-interviews'); fetchAllInterviews(); } });
                if (items.length === 0) return null;
                return (
                  <div className="bg-blue-50 border border-blue-200 rounded-2xl px-5 py-4">
                    <div className="flex items-center gap-2 mb-2.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse"></span>
                      <span className="text-[11px] font-bold text-blue-700 uppercase tracking-widest">Needs Your Attention</span>
                    </div>
                    <div className="space-y-2">
                      {items.map((item,i)=>(
                        <div key={i} className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2">
                            <span className="text-sm">{item.icon}</span>
                            <span className="text-xs text-gray-700 font-medium">{item.text}</span>
                          </div>
                          {item.action
                            ? <button onClick={item.action} className={`text-[10px] font-bold px-2.5 py-1 rounded-full border flex-shrink-0 ${item.pillColor}`}>{item.pill}</button>
                            : <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full border flex-shrink-0 ${item.pillColor}`}>{item.pill}</span>
                          }
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* ── Jobs-Focused Stats + Donut Chart ── */}
              {(()=>{
                // Company-wide all-time totals — intentionally decoupled from the period selector at top.
                // The period selector controls only the hero card (which shows trend vs prior period).
                // These KPI cards, the outcome donut, and the outcome rates show stable cumulative numbers.
                const allJds2      = jdByUser.flatMap(u=>u.jds||[]);
                const filtIvs      = allInterviews;
                const totalJobs    = allJds2.length;
                const aiIntake     = allJds2.filter(j=>j.source==='ai_meeting').length;
                const phase1Upload = allJds2.filter(j=>j.source!=='ai_meeting').length;
                const completed2   = filtIvs.filter(iv=>iv.status==='completed'||iv.status==='processed');
                const selected2    = filtIvs.filter(iv=>iv.hm_decision==='move_forward').length;
                const rejected2    = filtIvs.filter(iv=>iv.hm_decision==='rejected').length;
                const onHold2      = filtIvs.filter(iv=>iv.hm_decision==='on_hold').length;
                const base2        = completed2.length;
                const pct2 = (n) => base2>0 ? Math.round(n/base2*100) : 0;
                return (
                  <>
                    {/* Stat cards */}
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex flex-col gap-1.5 hover:shadow-md transition-shadow cursor-pointer" onClick={()=>navigate('/jobs')}>
                        <div className="flex items-center justify-between"><span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Total Jobs</span><span className="w-1.5 h-1.5 rounded-full bg-blue-400"></span></div>
                        <div className="text-3xl font-black text-blue-600">{totalJobs}</div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[10px] bg-green-50 text-green-700 border border-green-200 font-semibold px-2 py-0.5 rounded-full">Phase 1: {phase1Upload}</span>
                          <span className="text-[10px] bg-blue-50 text-blue-700 border border-blue-200 font-semibold px-2 py-0.5 rounded-full">AI Intake: {aiIntake}</span>
                        </div>
                      </div>
                      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex flex-col gap-1.5 hover:shadow-md transition-shadow cursor-pointer" onClick={()=>{setActiveMenu('my-interviews');fetchAllInterviews();}}>
                        <div className="flex items-center justify-between"><span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Total Interviews</span><span className="w-1.5 h-1.5 rounded-full bg-purple-400"></span></div>
                        <div className="text-3xl font-black text-purple-600">{filtIvs.length}</div>
                        <div className="text-[11px] text-gray-400">{completed2.length} completed / scored</div>
                      </div>
                      {[
                        { label:'Selected', val:selected2, color:'text-green-600', dot:'bg-green-500', sub:'Move forward' },
                        { label:'Rejected', val:rejected2, color:'text-red-500',   dot:'bg-red-400',   sub:'Not moving forward' },
                        { label:'On Hold',  val:onHold2,   color:'text-yellow-600',dot:'bg-yellow-400',sub:'Pending decision' },
                        { label:'Awaiting Decision', val:completed2.filter(iv=>!iv.hm_decision).length, color:'text-gray-500', dot:'bg-gray-300', sub:'Scored, no decision' },
                      ].map(c=>(
                        <div key={c.label} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex flex-col gap-1.5 hover:shadow-md transition-shadow cursor-pointer" onClick={()=>{setActiveMenu('my-interviews');fetchAllInterviews();}}>
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{c.label}</span>
                            <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`}></span>
                          </div>
                          <div className={`text-3xl font-black ${c.color}`}>{c.val}</div>
                          <div className="text-[11px] text-gray-400">{c.sub}</div>
                        </div>
                      ))}
                    </div>

                    {/* Donut Chart */}
                    {base2>0 && (()=>{
                      const segs = [
                        {label:'Selected',val:selected2,color:'#22c55e'},
                        {label:'Rejected',val:rejected2,color:'#f87171'},
                        {label:'On Hold', val:onHold2,  color:'#facc15'},
                        {label:'Awaiting',val:completed2.filter(iv=>!iv.hm_decision).length,color:'#d1d5db'},
                      ].filter(s=>s.val>0);
                      const total=segs.reduce((a,s)=>a+s.val,0);
                      const r=54,cx=70,cy=70,stroke=24,circ=2*Math.PI*r;
                      let offset=0;
                      const arcs=segs.map(s=>{const dash=(s.val/total)*circ;const arc={...s,dash,gap:circ-dash,offset};offset+=dash;return arc;});
                      const selPct=Math.round(selected2/base2*100);
                      return (
                        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                          <div className="flex items-center gap-2 mb-4">
                            <span className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Outcome Distribution</span>
                            <div className="flex-1 h-px bg-gray-100"></div>
                            <span className="text-[10px] text-gray-400">from {base2} scored interview{base2!==1?'s':''}</span>
                          </div>
                          <div className="flex items-center gap-8">
                            <div className="relative flex-shrink-0">
                              <svg width="140" height="140" viewBox="0 0 140 140">
                                <circle cx={cx} cy={cy} r={r} fill="none" stroke="#f3f4f6" strokeWidth={stroke}/>
                                {arcs.map((a,i)=>(
                                  <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={a.color} strokeWidth={stroke}
                                    strokeDasharray={`${a.dash} ${a.gap}`} strokeDashoffset={circ/4-a.offset} strokeLinecap="butt"
                                    style={{transition:'stroke-dasharray 0.6s ease'}}/>
                                ))}
                                <text x={cx} y={cy-8} textAnchor="middle" style={{fontSize:'22px',fontWeight:900,fill:'#111827'}}>{selPct}%</text>
                                <text x={cx} y={cy+12} textAnchor="middle" style={{fontSize:'10px',fill:'#9ca3af',fontWeight:600}}>selected</text>
                              </svg>
                            </div>
                            <div className="flex-1 space-y-3">
                              {segs.map(s=>(
                                <div key={s.label} className="flex items-center gap-3">
                                  <span className="w-3 h-3 rounded-full flex-shrink-0" style={{background:s.color}}></span>
                                  <div className="flex-1">
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="text-xs font-semibold text-gray-700">{s.label}</span>
                                      <span className="text-xs font-black text-gray-800">{Math.round(s.val/total*100)}%</span>
                                    </div>
                                    <div className="w-full bg-gray-100 rounded-full h-1.5">
                                      <div className="h-1.5 rounded-full transition-all duration-700" style={{width:`${Math.round(s.val/total*100)}%`,background:s.color}}></div>
                                    </div>
                                    <span className="text-[10px] text-gray-400">{s.val} candidate{s.val!==1?'s':''}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Outcome Rate bars */}
                    {base2>0 && (
                      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                        <div className="flex items-center gap-2 mb-4">
                          <span className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Outcome Rates</span>
                          <div className="flex-1 h-px bg-gray-100"></div>
                          <span className="text-[10px] text-gray-400">Based on {base2} completed interview{base2!==1?'s':''}</span>
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                          {[
                            {label:'Selected',pctVal:pct2(selected2),count:selected2,color:'bg-green-500',text:'text-green-700'},
                            {label:'Rejected',pctVal:pct2(rejected2),count:rejected2,color:'bg-red-400',  text:'text-red-600'},
                            {label:'On Hold', pctVal:pct2(onHold2),  count:onHold2,  color:'bg-yellow-400',text:'text-yellow-700'},
                          ].map(p=>(
                            <div key={p.label} className="flex flex-col gap-2">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-semibold text-gray-600">{p.label}</span>
                                <span className={`text-sm font-black ${p.text}`}>{p.pctVal}%</span>
                              </div>
                              <div className="w-full bg-gray-100 rounded-full h-2">
                                <div className={`${p.color} h-2 rounded-full transition-all`} style={{width:`${p.pctVal}%`}}></div>
                              </div>
                              <span className="text-[10px] text-gray-400">{p.count} of {base2}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}

              {/* ── Enhanced Recruitment Pipeline Funnel ── */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h3 className="text-sm font-bold text-gray-800">Company Recruitment Pipeline</h3>
                    <p className="text-[11px] text-gray-400 mt-0.5">End-to-end conversion across all teams</p>
                  </div>
                  <button onClick={()=>navigate('/jobs')} className="text-[11px] text-blue-600 font-semibold hover:underline">View All →</button>
                </div>
                {(()=>{
                  const allJds3 = jdByUser.flatMap(u=>u.jds||[]);
                  const roleSetup  = allJds3.length;
                  const intakeDone = allJds3.filter(j=>j.status==='completed').length;
                  const techScreen = allInterviews.length;
                  const scored     = allInterviews.filter(iv=>iv.status==='processed').length;
                  const selected3  = allInterviews.filter(iv=>iv.hm_decision==='move_forward').length;
                  if (roleSetup===0) return <div className="flex h-16 items-center justify-center text-gray-300 text-sm">No data yet</div>;
                  const cap = (n,d) => d>0?Math.min(100,Math.round(n/d*100)):0;
                  const stages = [
                    {label:'Role Setup',  icon:'📄', count:roleSetup,  color:'bg-blue-500',   bar:'bg-blue-100',   conv:null},
                    {label:'Intake Done', icon:'📞', count:intakeDone, color:'bg-indigo-500',  bar:'bg-indigo-100',  conv:cap(intakeDone,roleSetup)},
                    {label:'Interviewed', icon:'🎙️', count:techScreen, color:'bg-purple-500',  bar:'bg-purple-100',  conv:cap(techScreen,roleSetup)},
                    {label:'AI Scored',   icon:'🏆', count:scored,     color:'bg-orange-400',  bar:'bg-orange-100',  conv:cap(scored,techScreen||1)},
                    {label:'Selected',    icon:'✅', count:selected3,  color:'bg-green-500',   bar:'bg-green-100',   conv:cap(selected3,scored||1)},
                  ];
                  const maxCount=Math.max(...stages.map(s=>s.count),1);
                  return (
                    <div className="space-y-2">
                      {stages.map((s,i)=>(
                        <div key={s.label} className="flex items-center gap-4">
                          <div className="w-28 flex items-center gap-2 flex-shrink-0">
                            <span className="text-sm">{s.icon}</span>
                            <span className="text-xs font-semibold text-gray-600 truncate">{s.label}</span>
                          </div>
                          <div className={`flex-1 ${s.bar} rounded-lg h-8 relative overflow-hidden`}>
                            <div className={`${s.color} h-8 rounded-lg transition-all duration-700`} style={{width:`${Math.max(4,Math.round(s.count/maxCount*100))}%`}}></div>
                            <span className="absolute left-3 top-0 bottom-0 flex items-center text-xs font-black text-white mix-blend-difference">{s.count}</span>
                          </div>
                          <div className="w-16 flex-shrink-0 text-right">
                            {s.conv!==null
                              ? <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${s.conv>=50?'bg-green-50 text-green-700':s.conv>=25?'bg-yellow-50 text-yellow-700':'bg-red-50 text-red-600'}`}>{s.conv}%</span>
                              : <span className="text-xs text-gray-300">—</span>
                            }
                          </div>
                        </div>
                      ))}
                      <div className="flex items-center justify-end pt-1">
                        <span className="text-[10px] text-gray-400">Conversion % relative to previous stage</span>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* ── Timeline Activity + Upcoming Interviews ── */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Recent Activity Timeline */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest">Recent Activity</h3>
                    <button onClick={()=>{setActiveMenu('my-interviews');fetchAllInterviews();}} className="text-[11px] text-purple-500 font-semibold hover:underline">All →</button>
                  </div>
                  {(()=>{
                    const feed=[];
                    allInterviews.slice(0,6).forEach(iv=>{
                      const dec=iv.hm_decision;
                      const decLabel=dec==='move_forward'?'Selected':dec==='rejected'?'Rejected':dec==='on_hold'?'On Hold':null;
                      const decColor=dec==='move_forward'?'bg-green-100 text-green-700':dec==='rejected'?'bg-red-100 text-red-600':dec==='on_hold'?'bg-yellow-100 text-yellow-700':null;
                      const dotColor=iv.status==='processed'?'#22c55e':iv.status==='completed'?'#f97316':iv.status==='scheduled'?'#60a5fa':'#a78bfa';
                      feed.push({key:`iv-${iv.id}`,dot:dotColor,title:iv.candidate_name||'Candidate',sub:`${iv.role_type||'Role'} · ${iv.hiring_manager_name||'—'}`,score:iv.overall_score?`${Math.round(iv.overall_score)}/400`:null,decLabel,decColor,date:iv.scheduled_datetime?new Date(iv.scheduled_datetime).toLocaleDateString('en-US',{month:'short',day:'numeric'}):null});
                    });
                    jdByUser.slice(0,2).forEach(u=>u.jds.slice(0,2).forEach(j=>{
                      const dotColor=j.status==='completed'?'#22c55e':j.status==='processing'?'#facc15':'#f87171';
                      feed.push({key:`j-${j.id}`,dot:dotColor,title:j.jobTitle||j.job_title||'Role',sub:`${u.userName} · ${j.source==='ai_meeting'?'AI Intake':'Phase 1'}`,score:null,decLabel:j.status==='completed'?'Done':j.status==='processing'?'Processing':null,decColor:j.status==='completed'?'bg-green-100 text-green-700':'bg-yellow-100 text-yellow-700',date:j.created_at?new Date(j.created_at).toLocaleDateString('en-US',{month:'short',day:'numeric'}):null});
                    }));
                    if (feed.length===0) return <div className="flex h-20 items-center justify-center text-gray-300 text-sm">No activity yet</div>;
                    return (
                      <div className="space-y-0">
                        {feed.slice(0,7).map((item,i)=>(
                          <div key={item.key} className="flex items-start gap-3 py-2.5 border-b border-gray-50 last:border-0">
                            <div className="flex flex-col items-center gap-1 pt-0.5">
                              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{background:item.dot}}></span>
                              {i<feed.slice(0,7).length-1 && <div className="w-px h-full bg-gray-100 flex-1 min-h-[16px]"></div>}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-xs font-semibold text-gray-800 truncate">{item.title}</span>
                                <span className="text-[10px] text-gray-400 flex-shrink-0">{item.date}</span>
                              </div>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-[10px] text-gray-400 truncate">{item.sub}</span>
                                {item.score && <span className="text-[10px] font-black text-purple-600 flex-shrink-0">{item.score}</span>}
                                {item.decLabel && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 ${item.decColor}`}>{item.decLabel}</span>}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>

                {/* Upcoming Interviews */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest">Upcoming Interviews</h3>
                    <button onClick={()=>{setActiveMenu('my-interviews');fetchAllInterviews();}} className="text-[11px] text-purple-500 font-semibold hover:underline">All →</button>
                  </div>
                  {(()=>{
                    const now3=new Date();
                    const in7=new Date(now3.getTime()+7*86400000);
                    const upcoming=allInterviews
                      .filter(iv=>iv.status==='scheduled'&&iv.scheduled_datetime&&new Date(iv.scheduled_datetime)>=now3&&new Date(iv.scheduled_datetime)<=in7)
                      .sort((a,b)=>new Date(a.scheduled_datetime)-new Date(b.scheduled_datetime));
                    if (upcoming.length===0) return (
                      <div className="flex flex-col h-40 items-center justify-center gap-2 text-center">
                        <span className="text-3xl">📅</span>
                        <p className="text-sm font-semibold text-gray-600">No interviews this week</p>
                        <p className="text-xs text-gray-400">Scheduled interviews will appear here</p>
                      </div>
                    );
                    return (
                      <div className="space-y-2">
                        {upcoming.slice(0,5).map(iv=>{
                          const dt=new Date(iv.scheduled_datetime);
                          const isToday=dt.toDateString()===now3.toDateString();
                          const isTomorrow=dt.toDateString()===new Date(now3.getTime()+86400000).toDateString();
                          const dayLabel=isToday?'Today':isTomorrow?'Tomorrow':dt.toLocaleDateString('en-US',{weekday:'short',month:'short',day:'numeric'});
                          return (
                            <div key={iv.id} className={`flex items-center gap-3 p-3 rounded-xl border ${isToday?'border-purple-200 bg-purple-50':'border-gray-100 bg-gray-50'}`}>
                              <div className={`w-10 h-10 rounded-xl flex flex-col items-center justify-center flex-shrink-0 ${isToday?'bg-purple-600':'bg-gray-700'}`}>
                                <span className="text-[8px] font-bold text-white/70 uppercase">{dt.toLocaleDateString('en-US',{month:'short'})}</span>
                                <span className="text-sm font-black text-white leading-none">{dt.getDate()}</span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-xs font-bold text-gray-800 truncate">{iv.candidate_name||'Candidate'}</div>
                                <div className="text-[10px] text-gray-500 truncate">{iv.role_type||'Role'} · {dt.toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit'})}</div>
                              </div>
                              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${isToday?'bg-purple-200 text-purple-700':'bg-gray-200 text-gray-600'}`}>{dayLabel}</span>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* ── Tier 3: Insights Strip ── */}
              {(()=>{
                const completedIvs=allInterviews.filter(iv=>iv.status==='processed'||iv.status==='completed');
                const decided=completedIvs.filter(iv=>iv.hm_decision&&iv.hm_decision_updated_at&&(iv.ended_at||iv.updated_at));
                const avgDays=decided.length>0
                  ? Math.round(decided.reduce((acc,iv)=>{
                      const end=new Date(iv.ended_at||iv.updated_at);
                      const dec=new Date(iv.hm_decision_updated_at);
                      return acc+Math.max(0,(dec-end)/(1000*60*60*24));
                    },0)/decided.length*10)/10
                  : null;
                const selRate3=completedIvs.length>0?Math.round(allInterviews.filter(iv=>iv.hm_decision==='move_forward').length/completedIvs.length*100):null;
                const benchmark=selRate3!==null
                  ? selRate3>=25?{label:'Above average',color:'text-green-600',bg:'bg-green-50',border:'border-green-200',icon:'📈'}
                  : selRate3>=15?{label:'In range',color:'text-blue-600',bg:'bg-blue-50',border:'border-blue-200',icon:'✅'}
                  : {label:'Below average',color:'text-yellow-700',bg:'bg-yellow-50',border:'border-yellow-200',icon:'📉'}
                  : null;
                if (!avgDays&&!selRate3) return null;
                return (
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {avgDays!==null && (
                      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0"><span className="text-lg">⏱️</span></div>
                        <div>
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Avg Decision Time</p>
                          <p className="text-xl font-black text-indigo-600">{avgDays} <span className="text-sm font-semibold text-gray-500">days</span></p>
                          <p className="text-[10px] text-gray-400">after interview completion</p>
                        </div>
                      </div>
                    )}
                    {benchmark && (
                      <div className={`rounded-xl border ${benchmark.border} ${benchmark.bg} p-4 flex items-center gap-4`}>
                        <div className="w-10 h-10 rounded-xl bg-white/60 flex items-center justify-center flex-shrink-0"><span className="text-lg">{benchmark.icon}</span></div>
                        <div>
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Selection Rate</p>
                          <p className={`text-xl font-black ${benchmark.color}`}>{selRate3}%</p>
                          <p className="text-[10px] text-gray-500">{benchmark.label} · industry avg 15–25%</p>
                        </div>
                      </div>
                    )}
                    <div className="bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl p-4 flex flex-col gap-2">
                      <p className="text-[10px] font-bold text-blue-100 uppercase tracking-widest">Quick Actions</p>
                      {LEGACY_MEETINGS && <button onClick={()=>setActiveMenu('my-meetings')} className="text-left text-xs font-semibold text-white bg-white/15 hover:bg-white/25 rounded-lg px-3 py-2 transition-colors">+ New Role Setup</button>}
                      <button onClick={()=>navigate('/technical-screen?schedule=1')} className="text-left text-xs font-semibold text-white bg-white/15 hover:bg-white/25 rounded-lg px-3 py-2 transition-colors">+ Schedule Interview</button>
                    </div>
                  </div>
                );
              })()}

            </div>
          )}


          {/* ========== MY MEETINGS (uses jdByUser — same data as JD Processing) ========== */}
          {activeMenu === 'my-meetings' && (() => {
            // Build per-user combined map: AI meetings + JD uploads
            const meetingsByUser = {};
            // Step 1: seed from jdByUser (JD uploads + requirements from AI meetings)
            jdByUser.forEach(ug => {
              if (!meetingsByUser[ug.userId]) meetingsByUser[ug.userId] = { userId: ug.userId, userName: ug.userId === currentUserId ? 'Me (Admin)' : ug.userName, aiMeetings: [], jdUploads: [] };
              ug.jds.forEach(jd => meetingsByUser[ug.userId].jdUploads.push(jd));
            });
            // Step 2: merge AI meetings from companyMeetings
            companyMeetings.forEach(m => {
              const uid = m.scheduled_by_user_id;
              const uname = m.scheduled_by_name || 'Unknown';
              if (!meetingsByUser[uid]) meetingsByUser[uid] = { userId: uid, userName: uid === currentUserId ? 'Me (Admin)' : uname, aiMeetings: [], jdUploads: [] };
              meetingsByUser[uid].aiMeetings.push(m);
            });
            // Sort: admin first, then alphabetical
            let userGroups = Object.values(meetingsByUser);
            userGroups.sort((a, b) => {
              if (a.userId === currentUserId) return -1;
              if (b.userId === currentUserId) return 1;
              return a.userName.localeCompare(b.userName);
            });
            // Apply search
            const sq = searchFilter.toLowerCase();
            if (sq) userGroups = userGroups.filter(g => g.userName.toLowerCase().includes(sq));

            const totalAI = companyMeetings.length;
            const totalJD = jdByUser.reduce((s, u) => s + u.totalJDs, 0);

            return (
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">My Meetings</h1>
                  <p className="text-sm text-gray-500 mt-0.5">AI intake meetings &amp; JD uploads — all team members</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => {
                    const rows = [];
                    jdByUser.forEach(u => u.jds.forEach(j => rows.push({ User:u.userName, Type:j.source==='ai_meeting'?'Intake Call':'Role Setup', Title:j.jobTitle||'Untitled', 'Hiring Manager':j.hiringManagerName||'—', Date:j.date||'—', Status:j.status })));
                    companyMeetings.forEach(m => rows.push({ User:m.recruiter_name||'—', Type:'Intake Call', Title:m.meeting_title||'AI Meeting', 'Hiring Manager':m.hiring_manager_name||'—', Date:m.scheduled_date||'—', Status:m.status }));
                    exportToExcel(rows, 'Company_Meetings');
                  }} title="Export to Excel" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-50 text-green-700 hover:bg-green-100 border border-green-200 text-xs font-semibold transition-all">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                    XLS
                  </button>
                  <button onClick={() => {
                    const cols = ['User','Type','Title','Hiring Manager','Date','Status'];
                    const rows = [];
                    jdByUser.forEach(u => u.jds.forEach(j => rows.push([u.userName, j.source==='ai_meeting'?'Intake Call':'Role Setup', j.jobTitle||'Untitled', j.hiringManagerName||'—', j.date||'—', j.status])));
                    companyMeetings.forEach(m => rows.push([m.recruiter_name||'—','Intake Call', m.meeting_title||'AI Meeting', m.hiring_manager_name||'—', m.scheduled_date||'—', m.status]));
                    exportToPDF(cols, rows, 'Company Meetings Report', 'Company_Meetings');
                  }} title="Export to PDF" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 text-xs font-semibold transition-all">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"/></svg>
                    PDF
                  </button>
                  <button onClick={fetchCompanyMeetings} title="Refresh"
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 text-gray-500 hover:bg-gray-200 border border-gray-200 transition-all text-sm">🔄</button>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-4 gap-3">
                {[
                  ['Total',       totalAI + totalJD,  'from-gray-50 to-gray-100',    'text-gray-900'],
                  ['AI Meetings', totalAI,             'from-purple-50 to-purple-100','text-purple-800'],
                  ['JD Uploads',  totalJD,             'from-emerald-50 to-emerald-100','text-emerald-800'],
                  ['Users',       totalUsers,          'from-blue-50 to-blue-100',    'text-blue-800'],
                ].map(([label, val, bg, tc]) => (
                  <div key={label} className={`bg-gradient-to-br ${bg} rounded-2xl p-4 border border-gray-200 text-center`}>
                    <div className={`text-3xl font-black ${tc}`}>{val}</div>
                    <div className="text-xs text-gray-500 font-semibold mt-1">{label}</div>
                  </div>
                ))}
              </div>

              {/* Search */}
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
                <input type="text" value={searchFilter} onChange={e => setSearchFilter(e.target.value)}
                  placeholder="Search by user name..."
                  className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-400 focus:border-blue-400" />
              </div>

              {/* Grouped table */}
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">📋</span>
                    <p className="font-bold text-gray-900">Meetings by User</p>
                  </div>
                  <span className="text-xs text-gray-400">{totalUsers} user{totalUsers !== 1 ? 's' : ''} · {totalAI + totalJD} total</span>
                </div>

                {(isLoadingJDs || isLoadingMeetings) ? (
                  <div className="p-16 text-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-4 border-brand-blue border-t-transparent mx-auto"></div>
                    <p className="text-gray-400 mt-4 text-sm">Loading...</p>
                  </div>
                ) : userGroups.length === 0 ? (
                  <div className="p-16 text-center">
                    <div className="text-5xl mb-3">📭</div>
                    <p className="text-gray-400 text-sm">No meetings found.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {userGroups.map(ug => {
                      const isOwn = ug.userId === currentUserId;
                      const totalRows = ug.aiMeetings.length + ug.jdUploads.length;
                      return (
                        <div key={ug.userId}>
                          {/* User header — expand/collapse */}
                          <button onClick={() => toggleUserExpand(ug.userId)}
                            className="w-full px-6 py-4 hover:bg-blue-50 transition-colors flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-sm ${
                                expandedUsers[ug.userId] ? 'bg-gradient-to-br from-brand-blue to-blue-600' : 'bg-gradient-to-br from-gray-400 to-gray-500'
                              }`}>{ug.userName?.charAt(0) || 'U'}</div>
                              <div className="text-left">
                                <p className="font-bold text-gray-900 text-sm">{ug.userName}{isOwn ? '' : ''}</p>
                                <p className="text-xs text-gray-400">
                                  {ug.aiMeetings.length > 0 && `${ug.aiMeetings.length} AI meeting${ug.aiMeetings.length!==1?'s':''}`}
                                  {ug.aiMeetings.length > 0 && ug.jdUploads.length > 0 && ' · '}
                                  {ug.jdUploads.length > 0 && `${ug.jdUploads.length} JD upload${ug.jdUploads.length!==1?'s':''}`}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-lg font-bold text-brand-blue">{totalRows}</span>
                              <svg className={`w-4 h-4 text-gray-400 transition-transform ${expandedUsers[ug.userId] ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </div>
                          </button>

                          {expandedUsers[ug.userId] && (() => {
                            const MGP = 10;
                            const mgPage = meetGroupPages[ug.userId] || 1;
                            const allRows = [...ug.aiMeetings.map(m=>({type:'ai',data:m})), ...ug.jdUploads.map(j=>({type:'jd',data:j}))];
                            const mgTotal = Math.ceil(allRows.length / MGP);
                            const mgPaged = allRows.slice((mgPage-1)*MGP, mgPage*MGP);
                            const aiPaged = mgPaged.filter(r=>r.type==='ai').map(r=>r.data);
                            const jdPaged2 = mgPaged.filter(r=>r.type==='jd').map(r=>r.data);
                            return (
                            <div className="bg-gray-50 border-t border-gray-100">
                              <table className="w-full text-sm">
                                <thead className="bg-gray-100 border-b border-gray-200">
                                  <tr>
                                    {['Title','Source','HM','Status','Date','Actions'].map(h => (
                                      <th key={h} className="px-4 py-3 text-left text-xs font-bold text-gray-400 uppercase tracking-wide">{h}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                  {/* ── AI Meetings rows ── */}
                                  {aiPaged.map(m => {
                                    const st = (m.status||'').toLowerCase();
                                    const utc = new Date(`${m.scheduled_date}T${m.scheduled_time}:00Z`);
                                    const tz = m.hiring_manager_timezone || null;
                                    const dateStr = utc.toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric',...(tz?{timeZone:tz}:{})});
                                    const timeStr = utc.toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit',hour12:true,...(tz?{timeZone:tz}:{})});
                                    return (
                                      <tr key={`m-${m.id}`} className="hover:bg-white transition-colors">
                                        <td className="px-4 py-3"><p className="font-semibold text-gray-900 text-xs">{m.meeting_title||'AI Meeting'}</p></td>
                                        <td className="px-4 py-3">
                                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold border bg-purple-100 text-purple-800 border-purple-200">🤖 AI Meeting</span>
                                        </td>
                                        <td className="px-4 py-3 text-xs text-gray-500">{m.hiring_manager_name||'—'}</td>
                                        <td className="px-4 py-3">
                                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold uppercase border ${
                                            st==='completed'?'bg-green-100 text-green-700 border-green-200':
                                            st==='scheduled'?'bg-blue-100 text-blue-700 border-blue-200':
                                            st==='in_progress'?'bg-yellow-100 text-yellow-700 border-yellow-200':
                                            'bg-gray-100 text-gray-500 border-gray-200'
                                          }`}>{st}</span>
                                        </td>
                                        <td className="px-4 py-3 text-xs text-gray-500">
                                          <span>{dateStr}</span>
                                          <span className="block text-[11px] text-gray-400 mt-0.5">{timeStr}</span>
                                        </td>
                                        <td className="px-4 py-3">
                                          <div className="flex items-center gap-1.5">
                                            {/* 👁️ View — completed for all */}
                                            {(st==='completed'||st==='processed') && m.requirement_id && (
                                              <button onClick={() => navigate(`/view-requirement/${m.requirement_id}`)}
                                                title="View Results"
                                                className="w-8 h-8 flex items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 hover:bg-indigo-100 border border-indigo-200 transition-all text-sm">👁️</button>
                                            )}
                                            {/* Own meeting: Reschedule + Cancel */}
                                            {isOwn && st==='scheduled' && (
                                              <>
                                                <button
                                                  title="Reschedule Meeting"
                                                  onClick={() => { setRescheduleItem({...m, type:'meeting'}); setRescheduleDate(''); setRescheduleTime(''); }}
                                                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-orange-50 text-orange-600 hover:bg-orange-100 border border-orange-200 transition-all text-sm">📅</button>
                                                <button
                                                  title="Cancel Meeting"
                                                  onClick={async () => {
                                                    if (!window.confirm('Cancel this meeting?')) return;
                                                    const tok = localStorage.getItem('token');
                                                    const BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
                                                    const res = await fetch(`${BASE}/api/meetings/${m.id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${tok}` } });
                                                    if (res.ok || res.status === 204) fetchCompanyMeetings();
                                                  }}
                                                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-orange-50 text-orange-500 hover:bg-orange-100 border border-orange-200 transition-all text-sm">✕</button>
                                              </>
                                            )}
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                  {/* ── JD Upload rows ── */}
                                  {jdPaged2.map(jd => (
                                    <React.Fragment key={`jd-${jd.id}`}>
                                    <tr className="hover:bg-white transition-colors">
                                      <td className="px-4 py-3">
                                        <div className="flex items-center gap-1.5">
                                          <button
                                            onClick={()=>setExpandedRequirements(prev=>({...prev,[`jd-${jd.id}`]:!prev[`jd-${jd.id}`]}))}
                                            className="w-4 h-4 flex items-center justify-center text-gray-300 hover:text-gray-500 flex-shrink-0 transition-colors"
                                            title="Toggle activity trail">
                                            {expandedRequirements[`jd-${jd.id}`] ? '▾' : '▸'}
                                          </button>
                                          <p className="font-semibold text-gray-900 text-xs truncate max-w-[150px]">{jd.jobTitle||'Untitled'}</p>
                                        </div>
                                      </td>
                                      <td className="px-4 py-3">
                                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold border ${
                                          jd.source==='ai_meeting'?'bg-purple-100 text-purple-800 border-purple-200':'bg-emerald-100 text-emerald-800 border-emerald-200'
                                        }`}>{jd.source==='ai_meeting'?'🤖 Intake Call':'📋 Role Setup'}</span>
                                      </td>
                                      <td className="px-4 py-3 text-xs text-gray-500">{jd.hiringManagerName||'—'}</td>
                                      <td className="px-4 py-3">
                                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold uppercase border ${getStatusColor(jd.status)}`}>
                                          {getStatusIcon(jd.status)} {jd.status}
                                        </span>
                                      </td>
                                      <td className="px-4 py-3 text-xs text-gray-500">{jd.date}</td>
                                      <td className="px-4 py-3">
                                        <div className="flex items-center gap-1.5">
                                          <button onClick={() => navigate(`/view-requirement/${jd.id}`)}
                                            title="View Report"
                                            className="w-8 h-8 flex items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 hover:bg-indigo-100 border border-indigo-200 transition-all text-sm">👁️</button>
                                          {/* Delete — own only */}
                                          {isOwn && (
                                            <button title="Delete"
                                              onClick={async () => {
                                                if (!window.confirm(`Delete "${jd.jobTitle||'this requirement'}"?`)) return;
                                                const BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
                                                const tok = localStorage.getItem('token');
                                                const res = await fetch(`${BASE}/api/users/requirements/${jd.id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${tok}` } });
                                                if (res.ok) {
                                                  setJdByUser(prev => prev.map(g => ({
                                                    ...g, jds: g.jds.filter(j => j.id !== jd.id), totalJDs: g.jds.filter(j => j.id !== jd.id).length
                                                  })));
                                                } else {
                                                  const err = await res.json().catch(()=>({}));
                                                  alert('Delete failed: ' + (err.detail || res.status));
                                                }
                                              }}
                                              className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 text-red-500 hover:bg-red-100 border border-red-200 transition-all text-sm">🗑️</button>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                    {/* ── Activity Trail Row ── */}
                                    {expandedRequirements[`jd-${jd.id}`] && (
                                      <tr className="bg-gray-50">
                                        <td colSpan={6} className="px-6 py-3">
                                          <div className="space-y-1.5">
                                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Activity Trail</p>
                                            {(()=>{
                                              const trail = [];
                                              if (jd.source === 'ai_meeting') {
                                                trail.push({icon:'🗓️', label:`Intake call scheduled with ${jd.hiringManagerName||'—'}`, time: jd.date, color:'text-indigo-600'});
                                                if (jd.status==='in_progress') trail.push({icon:'🔴', label:'Call in progress', time:'', color:'text-red-500'});
                                                if (jd.status==='completed') trail.push({icon:'✅', label:'Call completed — AI report generated', time:'', color:'text-green-600'});
                                                if ((jd.status==='completed'||jd.status==='processed') && jd.id) trail.push({icon:'📋', label:'Screener document ready to view', time:'', color:'text-blue-600'});
                                              } else {
                                                trail.push({icon:'📄', label:'Role brief uploaded', time: jd.date, color:'text-emerald-600'});
                                                if (jd.status==='completed'||jd.status==='processed') trail.push({icon:'✅', label:'AI analysis complete — screener document generated', time:'', color:'text-green-600'});
                                                if (jd.status==='processing') trail.push({icon:'⏳', label:'AI is processing the brief', time:'', color:'text-yellow-600'});
                                                if (jd.approved_by_hiring_manager) trail.push({icon:'👍', label:'Approved by Hiring Manager', time:'', color:'text-blue-600'});
                                                else if (jd.status==='completed') trail.push({icon:'⏳', label:'Pending Hiring Manager review', time:'', color:'text-orange-500'});
                                              }
                                              return trail.map((t,i)=>(
                                                <div key={i} className="flex items-center gap-2">
                                                  <span className="text-xs flex-shrink-0">{t.icon}</span>
                                                  <span className={`text-xs font-medium flex-1 ${t.color}`}>{t.label}</span>
                                                  {t.time && <span className="text-[10px] text-gray-400 flex-shrink-0">{t.time}</span>}
                                                </div>
                                              ));
                                            })()}
                                          </div>
                                        </td>
                                      </tr>
                                    )}
                                    </React.Fragment>
                                  ))}
                                </tbody>
                              </table>
                              {mgTotal > 1 && (
                                <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 bg-white">
                                  <span className="text-xs text-gray-400">Page {mgPage} of {mgTotal} · {allRows.length} records</span>
                                  <div className="flex items-center gap-1">
                                    <button onClick={()=>setMeetGroupPages(p=>({...p,[ug.userId]:1}))} disabled={mgPage===1} className="px-2 py-1 rounded text-xs font-bold text-gray-500 hover:bg-gray-200 disabled:opacity-30">«</button>
                                    <button onClick={()=>setMeetGroupPages(p=>({...p,[ug.userId]:Math.max(1,mgPage-1)}))} disabled={mgPage===1} className="px-2 py-1 rounded text-xs font-bold text-gray-500 hover:bg-gray-200 disabled:opacity-30">‹</button>
                                    {Array.from({length:mgTotal},(_,i)=>i+1).filter(p=>p===1||p===mgTotal||Math.abs(p-mgPage)<=1).reduce((acc,p,idx,arr)=>{ if(idx>0&&p-arr[idx-1]>1) acc.push('…'); acc.push(p); return acc; },[]).map((p,i)=>(
                                      p==='…' ? <span key={`e${i}`} className="px-1 text-xs text-gray-400">…</span> :
                                      <button key={p} onClick={()=>setMeetGroupPages(prev=>({...prev,[ug.userId]:p}))} className={`w-7 h-7 rounded text-xs font-bold transition-all ${p===mgPage?'bg-blue-600 text-white':'text-gray-600 hover:bg-gray-200'}`}>{p}</button>
                                    ))}
                                    <button onClick={()=>setMeetGroupPages(p=>({...p,[ug.userId]:Math.min(mgTotal,mgPage+1)}))} disabled={mgPage===mgTotal} className="px-2 py-1 rounded text-xs font-bold text-gray-500 hover:bg-gray-200 disabled:opacity-30">›</button>
                                    <button onClick={()=>setMeetGroupPages(p=>({...p,[ug.userId]:mgTotal}))} disabled={mgPage===mgTotal} className="px-2 py-1 rounded text-xs font-bold text-gray-500 hover:bg-gray-200 disabled:opacity-30">»</button>
                                  </div>
                                </div>
                              )}
                            </div>
                            );
                          })()}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* ── Reschedule Modal ── */}
              {rescheduleItem && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
                  <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
                    <div className="bg-gradient-to-r from-brand-blue to-blue-600 px-6 py-4 flex items-center justify-between">
                      <div>
                        <h3 className="text-white font-bold text-lg">Reschedule Meeting</h3>
                        <p className="text-blue-100 text-sm mt-0.5 truncate max-w-xs">{rescheduleItem.meeting_title||'AI Meeting'}</p>
                      </div>
                      <button onClick={() => { setRescheduleItem(null); setRescheduleDate(''); setRescheduleTime(''); }}
                        className="text-white/80 hover:text-white text-2xl leading-none">✕</button>
                    </div>
                    <div className="px-6 py-5 space-y-4">
                      {rescheduleItem.hiring_manager_timezone && (
                        <div className="flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                          <span>🌍</span>
                          <span className="text-xs text-blue-700 font-medium">Timezone: {rescheduleItem.hiring_manager_timezone}</span>
                        </div>
                      )}
                      <div className="space-y-1">
                        <label className="text-xs font-bold text-gray-600 uppercase tracking-wide">New Date</label>
                        <input type="date" value={rescheduleDate} onChange={e => setRescheduleDate(e.target.value)}
                          min={new Date().toISOString().split('T')[0]}
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-400 outline-none" />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-bold text-gray-600 uppercase tracking-wide">New Time</label>
                        <input type="time" value={rescheduleTime} onChange={e => setRescheduleTime(e.target.value)}
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-400 outline-none" />
                      </div>
                    </div>
                    <div className="px-6 pb-5 flex gap-3">
                      <button onClick={() => { setRescheduleItem(null); setRescheduleDate(''); setRescheduleTime(''); }}
                        className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-semibold hover:bg-gray-50">Cancel</button>
                      <button
                        disabled={rescheduleLoading || !rescheduleDate || !rescheduleTime}
                        onClick={async () => {
                          if (!rescheduleDate || !rescheduleTime) { alert('Please select both date and time.'); return; }
                          setRescheduleLoading(true);
                          try {
                            const tok = localStorage.getItem('token');
                            const BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
                            const tz = rescheduleItem.hiring_manager_timezone || 'America/New_York';
                            const params = new URLSearchParams({ scheduled_date: rescheduleDate, scheduled_time: rescheduleTime, hm_timezone: tz });
                            const res = await fetch(`${BASE}/api/meetings/${rescheduleItem.id}?${params}`, {
                              method: 'PUT', headers: { 'Authorization': `Bearer ${tok}` }
                            });
                            if (res.ok) {
                              setRescheduleItem(null); setRescheduleDate(''); setRescheduleTime('');
                              fetchCompanyMeetings();
                            } else {
                              const err = await res.json().catch(()=>({}));
                              alert('Reschedule failed: ' + (err.detail || res.status));
                            }
                          } catch(e) { alert('Reschedule failed.'); }
                          finally { setRescheduleLoading(false); }
                        }}
                        className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-brand-blue to-blue-600 text-white text-sm font-bold hover:from-blue-700 hover:to-blue-700 disabled:opacity-50 transition-all">
                        {rescheduleLoading ? 'Saving...' : '📅 Save & Reschedule'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            );
          })()}

          {/* ========== MY INTERVIEWS ========== */}
          {activeMenu === 'my-interviews' && (
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">My Interviews</h1>
                  <p className="text-sm text-gray-500 mt-0.5">AI technical interviews — self &amp; all users</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => {
                    const data = allInterviews || [];
                    const rows = data.map(iv=>({ Candidate:iv.candidate_name||'—', Role:iv.role_type||'—', Level:iv.interview_level||'—', 'Hiring Manager':iv.hiring_manager_name||'—', Recruiter:iv.recruiter_name||'—', Status:iv.status||'—', Score:iv.overall_score?`${iv.overall_score}/400`:'—', Recommendation:iv.recommendation||'—' }));
                    exportToExcel(rows, 'Company_Interviews');
                  }} title="Export to Excel" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-50 text-green-700 hover:bg-green-100 border border-green-200 text-xs font-semibold transition-all">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                    XLS
                  </button>
                  <button onClick={() => {
                    const data = allInterviews || [];
                    const cols = ['Candidate','Role','Level','Hiring Manager','Recruiter','Status','Score','Recommendation'];
                    const rows = data.map(iv=>[iv.candidate_name||'—', iv.role_type||'—', iv.interview_level||'—', iv.hiring_manager_name||'—', iv.recruiter_name||'—', iv.status||'—', iv.overall_score?`${iv.overall_score}/400`:'—', iv.recommendation||'—']);
                    exportToPDF(cols, rows, 'Company Interviews Report', 'Company_Interviews');
                  }} title="Export to PDF" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 text-xs font-semibold transition-all">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"/></svg>
                    PDF
                  </button>
                  <button onClick={fetchAllInterviews} title="Refresh"
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 text-gray-500 hover:bg-gray-200 border border-gray-200 transition-all text-sm">🔄</button>
                </div>
              </div>

              {/* Stats row */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  ['Total',     totalInterviews,     'from-gray-50 to-gray-100',   'text-gray-900'],
                  ['Completed', interviewsCompleted, 'from-green-50 to-green-100', 'text-green-800'],
                  ['Scheduled', interviewsScheduled, 'from-blue-50 to-blue-100',   'text-blue-800'],
                ].map(([label, val, bg, tc]) => (
                  <div key={label} className={`bg-gradient-to-br ${bg} rounded-2xl p-4 border border-gray-200 text-center`}>
                    <div className={`text-3xl font-black ${tc}`}>{val}</div>
                    <div className="text-xs text-gray-500 font-semibold mt-1">{label}</div>
                  </div>
                ))}
              </div>

              {/* Search + filter */}
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
                  <input type="text" value={interviewsSearch} onChange={e => setInterviewsSearch(e.target.value)}
                    placeholder="Search candidate, role, or hiring manager..."
                    className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-400 focus:border-blue-400" />
                </div>
                <div className="flex gap-1.5">
                  {[['all','All'],['scheduled','Scheduled'],['completed','Completed']].map(([k,l]) => (
                    <button key={k} onClick={() => setInterviewsFilter(k)}
                      className={`px-3 py-2 rounded-lg text-xs font-bold transition-all ${interviewsFilter===k?'bg-blue-600 text-white shadow-sm':'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>

              {/* Grouped by user with expand/collapse */}
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                {isLoadingInterviews ? (
                  <div className="p-16 text-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-4 border-purple-500 border-t-transparent mx-auto"></div>
                    <p className="text-gray-400 mt-4 text-sm">Loading interviews...</p>
                  </div>
                ) : interviewGroups.length === 0 ? (
                  <div className="p-16 text-center">
                    <div className="text-5xl mb-3">📭</div>
                    <p className="text-gray-400 text-sm">No interviews found.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {interviewGroups.map(group => {
                      const groupFiltered = filterInterviews(group.items);
                      const isExpanded = expandedInterviewUsers[group.userId];
                      return (
                        <div key={group.userId}>
                          <button onClick={() => setExpandedInterviewUsers(p => ({ ...p, [group.userId]: !p[group.userId] }))}
                            className="w-full px-6 py-4 hover:bg-purple-50 transition-colors flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-sm ${
                                isExpanded ? 'bg-gradient-to-br from-purple-500 to-indigo-600' : 'bg-gradient-to-br from-gray-400 to-gray-500'
                              }`}>{group.userName.charAt(0)}</div>
                              <div className="text-left">
                                <p className="font-bold text-gray-900 text-sm">{group.userName}</p>
                                <p className="text-xs text-gray-400">{group.items.length} interview{group.items.length !== 1 ? 's' : ''}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-lg font-bold text-purple-600">{group.items.length}</span>
                              <svg className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </div>
                          </button>

                          {isExpanded && (
                            <div className="bg-gray-50 border-t border-gray-100">
                              {groupFiltered.length === 0 ? (
                                <p className="text-center text-sm text-gray-400 py-6">No interviews match current filter.</p>
                              ) : (() => {
                                const GP = 10;
                                const gPage = groupPages[group.userId] || 1;
                                const gTotal = Math.ceil(groupFiltered.length / GP);
                                const gPaged = groupFiltered.slice((gPage-1)*GP, gPage*GP);
                                return (
                                <>
                                <table className="w-full text-sm">
                                  <thead className="bg-gray-100 border-b border-gray-200">
                                    <tr>
                                      {['Candidate','Role / Stack','Level','Hiring Manager','Scheduled','Status','Score','HM Decision','Actions'].map(h => (
                                        <th key={h} className="px-4 py-3 text-left text-xs font-bold text-gray-400 uppercase tracking-wide">{h}</th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-100">
                                    {gPaged.map(iv => (
                                      <tr key={iv.id} className="hover:bg-white transition-colors">
                                        <td className="px-4 py-3">
                                          <p className="font-semibold text-gray-900 text-xs">{iv.candidate_name || '—'}</p>
                                          <p className="text-[10px] text-gray-400">{iv.candidate_email || ''}</p>
                                        </td>
                                        <td className="px-4 py-3">
                                          <p className="text-xs text-gray-700 font-medium">{iv.role_type || '—'}</p>
                                          {iv.tech_stack && <p className="text-[10px] text-gray-400 truncate max-w-[140px]">{iv.tech_stack}</p>}
                                          {iv.job_title && (
                                            <p className="text-[10px] text-gray-400 mt-0.5 whitespace-nowrap">
                                              {iv.job_title}{iv.req_id ? ` · ${iv.req_id}` : ''}
                                            </p>
                                          )}
                                        </td>
                                        <td className="px-4 py-3">
                                          <span className="text-xs text-gray-600 capitalize">{iv.interview_level || '—'}</span>
                                        </td>
                                        <td className="px-4 py-3">
                                          <p className="text-xs text-gray-700">{iv.hiring_manager_name || iv.hm_name || '—'}</p>
                                        </td>
                                        <td className="px-4 py-3 text-xs text-gray-600">
                                          {iv.scheduled_datetime ? (() => {
                                            const tz = iv.candidate_timezone || null;
                                            const dt = new Date(iv.scheduled_datetime);
                                            const opts = { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit', hour12:true, ...(tz ? { timeZone: tz } : {}) };
                                            return dt.toLocaleString('en-US', opts);
                                          })() : '—'}
                                        </td>
                                        <td className="px-4 py-3">
                                          <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                                            (iv.status||'').toLowerCase()==='completed'?'bg-green-100 text-green-700':
                                            (iv.status||'').toLowerCase()==='scored'?'bg-teal-100 text-teal-700':
                                            (iv.status||'').toLowerCase()==='scheduled'?'bg-blue-100 text-blue-700':
                                            (iv.status||'').toLowerCase()==='in_progress'?'bg-yellow-100 text-yellow-700':
                                            'bg-gray-100 text-gray-500'
                                          }`}>{(iv.status||'pending').replace('_',' ')}</span>
                                        </td>
                                        <td className="px-4 py-3">
                                          {iv.overall_score != null
                                            ? <span className="text-xs font-bold text-purple-700">{iv.overall_score}%</span>
                                            : <span className="text-xs text-gray-400">—</span>
                                          }
                                        </td>
                                        {/* ── HM DECISION COLUMN ── */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                          {iv.hm_decision ? (() => {
                                            const cfg = {
                                              move_forward: { label: 'Move Forward', icon: '✅', cls: 'bg-green-100 text-green-700 border-green-300' },
                                              on_hold:      { label: 'On Hold',      icon: '⏸️', cls: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
                                              rejected:     { label: 'Rejected',     icon: '❌', cls: 'bg-red-100 text-red-700 border-red-300' }
                                            }[iv.hm_decision] || { label: iv.hm_decision, icon: '•', cls: 'bg-gray-100 text-gray-600 border-gray-300' };
                                            return (
                                              <div className="group relative inline-block">
                                                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold border ${cfg.cls}`}>
                                                  {cfg.icon} {cfg.label}
                                                </span>
                                                {iv.hm_comment && (
                                                  <div className="absolute bottom-full left-0 mb-2 w-56 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20">
                                                    <p className="font-semibold mb-1 text-gray-300">HM Comment</p>
                                                    <p className="leading-relaxed">{iv.hm_comment}</p>
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          })() : <span className="text-gray-300 text-xs">—</span>}
                                        </td>
                                        <td className="px-4 py-3">
                                          <div className="flex items-center gap-1.5">
                                            {/* 👁️ View — only completed/scored */}
                                            {(['completed','scored','processed'].includes((iv.status||'').toLowerCase())) && (
                                              <button onClick={() => navigate(`/phase3/interview/${iv.id}`)}
                                                title="View Report"
                                                className="w-8 h-8 flex items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 hover:bg-indigo-100 border border-indigo-200 transition-all text-sm">
                                                👁️
                                              </button>
                                            )}
                                            {/* 🗑️ Delete — completed/processed/in_progress, own records only */}
                                            {(['completed','scored','processed','in_progress'].includes((iv.status||'').toLowerCase())) && iv.recruiter_id === currentUserId && (
                                              <button title="Delete Interview Record"
                                                onClick={async () => {
                                                  if (!window.confirm(`Delete interview record for ${iv.candidate_name}? The record will be archived.`)) return;
                                                  const BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
                                                  const tok = localStorage.getItem('token');
                                                  const res = await fetch(`${BASE}/api/phase3/interview/${iv.id}/cancel`, { method: 'POST', headers: { 'Authorization': `Bearer ${tok}` }});
                                                  if (res.ok) setAllInterviews(prev => prev.filter(x => x.id !== iv.id));
                                                  else fetchAllInterviews();
                                                }}
                                                className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 text-red-500 hover:bg-red-100 border border-red-200 transition-all text-sm">
                                                🗑️
                                              </button>
                                            )}
                                            {/* ✏️ Edit / 📧 Resend / 🗑️ Delete — only for MY interviews (recruiter_id === currentUserId) */}
                                            {iv.recruiter_id === currentUserId && (iv.status||'').toLowerCase() === 'scheduled' && (
                                              <>
                                                <button title="Edit Interview" onClick={() => openEditInterview(iv)}
                                                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-100 border border-amber-200 transition-all text-sm">
                                                  ✏️
                                                </button>
                                                <button title="Resend Invite"
                                                  onClick={async () => {
                                                    const BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
                                                    const tok = localStorage.getItem('token');
                                                    await fetch(`${BASE}/api/phase3/interview/${iv.id}/send-invite`, { method: 'POST', headers: { 'Authorization': `Bearer ${tok}` }});
                                                    fetchAllInterviews();
                                                  }}
                                                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200 transition-all text-sm">
                                                  📧
                                                </button>
                                                <button title="Cancel Interview"
                                                  onClick={async () => {
                                                    if (!window.confirm('Cancel this interview? It will be removed from the schedule.')) return;
                                                    const BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
                                                    const tok = localStorage.getItem('token');
                                                    const res = await fetch(`${BASE}/api/phase3/interview/${iv.id}/cancel`, { method: 'POST', headers: { 'Authorization': `Bearer ${tok}` }});
                                                    if (res.ok) setAllInterviews(prev => prev.filter(x => x.id !== iv.id));
                                                    else fetchAllInterviews();
                                                  }}
                                                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-orange-50 text-orange-500 hover:bg-orange-100 border border-orange-200 transition-all text-sm">
                                                  ✕
                                                </button>
                                              </>
                                            )}
                                            {/* No actions available */}
                                            {!(['completed','scored','processed','in_progress'].includes((iv.status||'').toLowerCase())) && iv.recruiter_id !== currentUserId && (
                                              <span className="text-xs text-gray-300">—</span>
                                            )}
                                            {(['completed','scored','processed','in_progress'].includes((iv.status||'').toLowerCase())) && iv.recruiter_id !== currentUserId && (
                                              <span className="text-xs text-gray-300">—</span>
                                            )}
                                          </div>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                                {gTotal > 1 && (
                                  <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 bg-white">
                                    <span className="text-xs text-gray-400">Page {gPage} of {gTotal} · {groupFiltered.length} interviews</span>
                                    <div className="flex items-center gap-1">
                                      <button onClick={()=>setGroupPages(p=>({...p,[group.userId]:1}))} disabled={gPage===1} className="px-2 py-1 rounded text-xs font-bold text-gray-500 hover:bg-gray-200 disabled:opacity-30">«</button>
                                      <button onClick={()=>setGroupPages(p=>({...p,[group.userId]:Math.max(1,gPage-1)}))} disabled={gPage===1} className="px-2 py-1 rounded text-xs font-bold text-gray-500 hover:bg-gray-200 disabled:opacity-30">‹</button>
                                      {Array.from({length:gTotal},(_,i)=>i+1).filter(p=>p===1||p===gTotal||Math.abs(p-gPage)<=1).reduce((acc,p,idx,arr)=>{ if(idx>0&&p-arr[idx-1]>1) acc.push('…'); acc.push(p); return acc; },[]).map((p,i)=>(
                                        p==='…' ? <span key={`e${i}`} className="px-1 text-xs text-gray-400">…</span> :
                                        <button key={p} onClick={()=>setGroupPages(prev=>({...prev,[group.userId]:p}))} className={`w-7 h-7 rounded text-xs font-bold transition-all ${p===gPage?'bg-purple-600 text-white':'text-gray-600 hover:bg-gray-200'}`}>{p}</button>
                                      ))}
                                      <button onClick={()=>setGroupPages(p=>({...p,[group.userId]:Math.min(gTotal,gPage+1)}))} disabled={gPage===gTotal} className="px-2 py-1 rounded text-xs font-bold text-gray-500 hover:bg-gray-200 disabled:opacity-30">›</button>
                                      <button onClick={()=>setGroupPages(p=>({...p,[group.userId]:gTotal}))} disabled={gPage===gTotal} className="px-2 py-1 rounded text-xs font-bold text-gray-500 hover:bg-gray-200 disabled:opacity-30">»</button>
                                    </div>
                                  </div>
                                )}
                                </>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* ══════════ EDIT INTERVIEW MODAL ══════════ */}
              {editingInterview && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                  onClick={(e)=>{ if(e.target===e.currentTarget) setEditingInterview(null); }}>
                  <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                    <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-6 rounded-t-2xl flex items-center justify-between">
                      <div>
                        <h2 className="text-xl font-bold text-white">Edit Interview</h2>
                        <p className="text-amber-100 text-sm mt-0.5">{editingInterview.candidate_name} · {editingInterview.role_type}</p>
                      </div>
                      <button onClick={()=>setEditingInterview(null)}
                        className="w-9 h-9 flex items-center justify-center rounded-xl bg-white/20 text-white hover:bg-white/30 transition-all text-lg font-bold">✕</button>
                    </div>
                    <div className="p-6 space-y-5">
                      {editError && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm font-medium">❌ {editError}</div>}
                      {editSuccess && <div className="bg-green-50 border border-green-200 text-green-700 rounded-xl px-4 py-3 text-sm font-medium">✅ {editSuccess}</div>}
                      <div>
                        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Candidate</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div><label className="block text-sm font-semibold text-gray-700 mb-1">Name</label>
                            <input type="text" value={editForm.candidate_name||''} onChange={e=>setEditForm(f=>({...f,candidate_name:e.target.value}))}
                              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-400" /></div>
                          <div><label className="block text-sm font-semibold text-gray-700 mb-1">Email</label>
                            <input type="email" value={editForm.candidate_email||''} onChange={e=>setEditForm(f=>({...f,candidate_email:e.target.value}))}
                              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-400" /></div>
                        </div>
                      </div>
                      <div>
                        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Hiring Manager</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div><label className="block text-sm font-semibold text-gray-700 mb-1">Name</label>
                            <input type="text" value={editForm.hiring_manager_name||''} onChange={e=>setEditForm(f=>({...f,hiring_manager_name:e.target.value}))}
                              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-400" /></div>
                          <div><label className="block text-sm font-semibold text-gray-700 mb-1">Email</label>
                            <input type="email" value={editForm.hiring_manager_email||''} onChange={e=>setEditForm(f=>({...f,hiring_manager_email:e.target.value}))}
                              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-400" /></div>
                        </div>
                      </div>
                      <div>
                        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Schedule</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          <div><label className="block text-sm font-semibold text-gray-700 mb-1">Date</label>
                            <input type="date" value={editForm.scheduled_date||''} onChange={e=>setEditForm(f=>({...f,scheduled_date:e.target.value}))}
                              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-400" /></div>
                          <div><label className="block text-sm font-semibold text-gray-700 mb-1">Time</label>
                            <input type="time" value={editForm.scheduled_time||''} onChange={e=>setEditForm(f=>({...f,scheduled_time:e.target.value}))}
                              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-400" /></div>
                          <div><label className="block text-sm font-semibold text-gray-700 mb-1">Duration (min)</label>
                            <select value={editForm.duration_minutes||60} onChange={e=>setEditForm(f=>({...f,duration_minutes:e.target.value}))}
                              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-400">
                              {[30,45,60,75,90].map(d=><option key={d} value={d}>{d} min</option>)}
                            </select></div>
                        </div>
                      </div>
                      <div>
                        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Interview Config</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          <div><label className="block text-sm font-semibold text-gray-700 mb-1">Level</label>
                            <select value={editForm.interview_level||'intermediate'} onChange={e=>setEditForm(f=>({...f,interview_level:e.target.value}))}
                              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-400">
                              <option value="basic">Basic (0–2 yrs)</option>
                              <option value="intermediate">Intermediate (3–6 yrs)</option>
                              <option value="advanced">Advanced (7+ yrs)</option>
                            </select></div>
                          <div><label className="block text-sm font-semibold text-gray-700 mb-1">Coding Mode</label>
                            <select value={editForm.coding_mode||'verbal_pseudocode'} onChange={e=>setEditForm(f=>({...f,coding_mode:e.target.value}))}
                              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-400">
                              <option value="verbal_pseudocode">Verbal / Pseudocode</option>
                              <option value="live_coding">Live Coding</option>
                            </select></div>
                          <div><label className="block text-sm font-semibold text-gray-700 mb-1">Proctoring</label>
                            <select value={editForm.proctoring_strictness||'medium'} onChange={e=>setEditForm(f=>({...f,proctoring_strictness:e.target.value}))}
                              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-400">
                              <option value="low">Low</option>
                              <option value="medium">Medium</option>
                              <option value="high">High</option>
                            </select></div>
                        </div>
                      </div>
                      <div>
                        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Role</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div><label className="block text-sm font-semibold text-gray-700 mb-1">Role Type</label>
                            <input type="text" value={editForm.role_type||''} onChange={e=>setEditForm(f=>({...f,role_type:e.target.value}))}
                              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-400" /></div>
                          <div><label className="block text-sm font-semibold text-gray-700 mb-1">Tech Stack</label>
                            <input type="text" value={editForm.tech_stack||''} onChange={e=>setEditForm(f=>({...f,tech_stack:e.target.value}))}
                              placeholder="e.g. Python, React, AWS"
                              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-400" /></div>
                        </div>
                      </div>
                      <div className="flex gap-3 pt-2">
                        <button onClick={()=>setEditingInterview(null)}
                          className="flex-1 py-3 rounded-xl border-2 border-gray-200 text-gray-600 font-bold text-sm hover:bg-gray-50 transition-all">Cancel</button>
                        <button onClick={handleSaveEditInterview} disabled={editLoading}
                          className="flex-1 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold text-sm hover:from-amber-600 hover:to-orange-600 transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                          {editLoading ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>Saving...</> : '💾 Save Changes'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ========== ESSENTIAL (Upload JD) ========== */}
          {activeMenu === 'essential' && (
            <div className="space-y-6">
              {/* ── Header ── */}
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">New Requirement</h1>
                  <p className="text-sm text-gray-500 mt-0.5">Select a hiring manager, then upload your JD and intake recording</p>
                </div>
                <div className="hidden sm:flex items-center gap-1 bg-gray-100 rounded-full px-4 py-1.5">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${essentialHmId ? 'bg-brand-blue text-white' : 'bg-white text-gray-400 border border-gray-200'}`}>1</span>
                  <div className={`w-8 h-0.5 ${essentialHmId ? 'bg-brand-blue' : 'bg-gray-200'}`}></div>
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${jdFile ? 'bg-brand-blue text-white' : 'bg-white text-gray-400 border border-gray-200'}`}>2</span>
                  <div className={`w-8 h-0.5 ${jdFile ? 'bg-brand-blue' : 'bg-gray-200'}`}></div>
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${audioFile ? 'bg-brand-green text-white' : 'bg-white text-gray-400 border border-gray-200'}`}>3</span>
                </div>
              </div>

              {/* ── Alerts ── */}
              {uploadSuccess && (
                <div className="bg-green-50 border border-green-300 rounded-xl p-4 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/></svg>
                  </div>
                  <p className="text-green-700 font-semibold text-sm">Files processed successfully!</p>
                </div>
              )}
              {uploadError && (
                <div className="bg-red-50 border border-red-300 rounded-xl p-4 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
                  </div>
                  <p className="text-red-700 font-semibold text-sm">{uploadError}</p>
                </div>
              )}

              {/* ── Main Upload Card ── */}
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">

                {/* Step 1 — Hiring Manager */}
                <div className="p-6 border-b border-gray-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${essentialHmId ? 'bg-brand-blue text-white' : 'bg-blue-50 text-brand-blue border-2 border-blue-200'}`}>
                      {essentialHmId ? (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/></svg>
                      ) : '1'}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Select Hiring Manager <span className="text-red-500">*</span></p>
                      <p className="text-xs text-gray-400">Who is this job opening for?</p>
                    </div>
                  </div>
                  {isLoadingManagers ? (
                    <div className="flex items-center gap-2 text-gray-400 text-sm py-2 pl-11">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-brand-blue border-t-transparent"></div>
                      Loading hiring managers...
                    </div>
                  ) : hiringManagers.length === 0 ? (
                    <div className="ml-11 text-sm text-red-500 bg-red-50 border border-red-100 rounded-lg p-3">
                      No hiring managers found. Give someone the "Create jobs" permission in User Management first.
                    </div>
                  ) : (
                    <div className="ml-11">
                      <div className="relative">
                        <select
                          value={essentialHmId}
                          onChange={e => {
                            const hm = hiringManagers.find(h => String(h.id) === e.target.value);
                            setEssentialHmId(e.target.value);
                            setEssentialHmName(hm ? hm.full_name : '');
                            setUploadError('');
                          }}
                          className="w-full appearance-none px-4 py-3 pr-10 border-2 border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-brand-blue focus:border-brand-blue transition-all bg-white text-gray-800"
                        >
                          <option value="">— Select a Hiring Manager —</option>
                          {hiringManagers.map(hm => (
                            <option key={hm.id} value={hm.id}>{hm.full_name} · {hm.email}</option>
                          ))}
                        </select>
                        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/></svg>
                        </span>
                      </div>
                      {essentialHmId && (
                        <div className="mt-2 flex items-center gap-2 text-xs text-brand-blue">
                          <div className="w-5 h-5 rounded-full bg-brand-blue text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                            {essentialHmName.charAt(0).toUpperCase()}
                          </div>
                          <span className="font-medium">{essentialHmName}</span>
                          <span className="text-gray-400">selected</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Step 2 — Job Description */}
                <div className={`p-6 border-b border-gray-100 transition-opacity ${!essentialHmId ? 'opacity-40 pointer-events-none' : ''}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${jdFile ? 'bg-brand-blue text-white' : 'bg-blue-50 text-brand-blue border-2 border-blue-200'}`}>
                      {jdFile ? (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/></svg>
                      ) : '2'}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Job Description <span className="text-red-500">*</span></p>
                      <p className="text-xs text-gray-400">PDF, DOC, DOCX, or TXT</p>
                    </div>
                    {jdFile && <span className="ml-auto text-xs text-brand-green font-semibold bg-green-50 border border-green-200 rounded-full px-3 py-1">✓ Ready</span>}
                  </div>
                  <div className="ml-11">
                    <input type="file" id="jd-upload" accept=".pdf,.doc,.docx,.txt" onChange={handleJdFileChange} className="hidden" />
                    <label htmlFor="jd-upload"
                      className={`flex items-center gap-4 border-2 border-dashed rounded-xl px-5 py-4 cursor-pointer transition-all hover:shadow-sm ${jdFile ? 'border-brand-green bg-green-50' : 'border-gray-200 hover:border-brand-blue hover:bg-blue-50'}`}>
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${jdFile ? 'bg-green-100' : 'bg-blue-100'}`}>
                        <svg className={`w-5 h-5 ${jdFile ? 'text-brand-green' : 'text-brand-blue'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        {jdFile ? (
                          <><p className="text-sm font-semibold text-brand-green truncate">{jdFile.name}</p><p className="text-xs text-gray-400">{(jdFile.size/1024).toFixed(1)} KB</p></>
                        ) : (
                          <><p className="text-sm font-medium text-gray-700">Click to upload Job Description</p><p className="text-xs text-gray-400">or drag and drop here</p></>
                        )}
                      </div>
                      {jdFile && (
                        <button type="button" onClick={e=>{e.preventDefault();setJdFile(null);}} className="flex-shrink-0 text-gray-300 hover:text-red-400 transition-colors">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
                        </button>
                      )}
                    </label>
                  </div>
                </div>

                {/* Step 3 — Audio / Video (optional) */}
                <div className={`p-6 transition-opacity ${!essentialHmId ? 'opacity-40 pointer-events-none' : ''}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${audioFile ? 'bg-brand-green text-white' : 'bg-gray-100 text-gray-400 border-2 border-gray-200'}`}>
                      {audioFile ? (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/></svg>
                      ) : '3'}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Intake Recording <span className="text-red-500">*</span></p>
                      <p className="text-xs text-gray-400">MP3, WAV, OGG, FLAC, WebM</p>
                    </div>
                    {audioFile && <span className="ml-auto text-xs text-brand-green font-semibold bg-green-50 border border-green-200 rounded-full px-3 py-1">✓ Ready</span>}
                  </div>
                  <div className="ml-11">
                    <input type="file" id="audio-upload" accept="audio/*,.mp3,.wav,.webm,.ogg,.flac" onChange={handleAudioFileChange} className="hidden" />
                    <label htmlFor="audio-upload"
                      className={`flex items-center gap-4 border-2 border-dashed rounded-xl px-5 py-4 cursor-pointer transition-all hover:shadow-sm ${audioFile ? 'border-brand-green bg-green-50' : 'border-gray-200 hover:border-brand-green hover:bg-green-50'}`}>
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${audioFile ? 'bg-green-100' : 'bg-gray-100'}`}>
                        <svg className={`w-5 h-5 ${audioFile ? 'text-brand-green' : 'text-gray-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"/>
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        {audioFile ? (
                          <><p className="text-sm font-semibold text-brand-green truncate">{audioFile.name}</p><p className="text-xs text-gray-400">{(audioFile.size/1024/1024).toFixed(2)} MB</p></>
                        ) : (
                          <><p className="text-sm font-medium text-gray-700">Click to upload intake recording</p><p className="text-xs text-gray-400">Required for AI-powered extraction accuracy</p></>
                        )}
                      </div>
                      {audioFile && (
                        <button type="button" onClick={e=>{e.preventDefault();setAudioFile(null);}} className="flex-shrink-0 text-gray-300 hover:text-red-400 transition-colors">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
                        </button>
                      )}
                    </label>
                  </div>
                </div>

                {/* Process Button */}
                <div className="p-6 bg-gray-50 border-t border-gray-100">
                  {(essentialHmId || jdFile) && (
                    <div className="mb-4 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                      <span className="font-medium text-gray-700">Ready to process:</span>
                      {essentialHmId && <span className="bg-blue-50 text-blue-600 border border-blue-100 rounded-full px-2.5 py-1 font-medium">👤 {essentialHmName}</span>}
                      {jdFile && <span className="bg-indigo-50 text-indigo-600 border border-indigo-100 rounded-full px-2.5 py-1 font-medium">📄 {jdFile.name}</span>}
                      {audioFile && <span className="bg-green-50 text-green-600 border border-green-100 rounded-full px-2.5 py-1 font-medium">🎙️ {audioFile.name}</span>}
                    </div>
                  )}
                  <button
                    onClick={() => handleProcessFiles(false)}
                    disabled={isProcessing || !jdFile || !essentialHmId || !audioFile}
                    className="w-full bg-gradient-to-r from-brand-blue to-blue-700 hover:from-brand-blue/90 hover:to-blue-700/90 text-white font-bold py-4 rounded-xl transition-all shadow-md hover:shadow-lg disabled:opacity-40 disabled:cursor-not-allowed text-sm tracking-wide"
                  >
                    {isProcessing ? (
                      <span className="flex items-center justify-center gap-3">
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                        AI is analyzing your files...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
                        Process & Generate Requirements
                      </span>
                    )}
                  </button>
                  {!essentialHmId && <p className="text-center text-xs text-gray-400 mt-2">Select a hiring manager to continue</p>}
                  {essentialHmId && !jdFile && <p className="text-center text-xs text-gray-400 mt-2">Upload a Job Description to continue</p>}

                  {/* Async processing indicator */}
                  {pollingRequirementId && pollingStatus === 'processing' && (
                    <div className="mt-4 bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center gap-3">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-500 border-t-transparent flex-shrink-0"></div>
                      <div>
                        <p className="text-sm font-semibold text-blue-700">AI is analyzing your files...</p>
                        <p className="text-xs text-blue-500 mt-0.5">This takes 30–60 seconds. You can navigate away — results will be ready when you return.</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ========== PROFESSIONAL (Schedule Meeting - Inline) ========== */}
          {activeMenu === 'professional' && isPlanAccessible('professional') && (
            <div className="space-y-6">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Professional Plan</h1>
                <p className="text-sm text-gray-600 mt-0.5">Schedule AI-powered intake meetings with hiring managers</p>
              </div>

              {scheduleSuccess && (
                <div className="bg-green-50 border-2 border-green-400 rounded-2xl p-5 flex items-center gap-3">
                  <span className="text-2xl">✅</span>
                  <p className="text-green-700 font-bold">{scheduleSuccess}</p>
                </div>
              )}
              {scheduleError && (
                <div className="bg-red-50 border-2 border-red-400 rounded-2xl p-5 flex items-center gap-3">
                  <span className="text-2xl">❌</span>
                  <p className="text-red-700 font-bold">{scheduleError}</p>
                </div>
              )}

              <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
                <h2 className="text-lg font-bold text-gray-900 mb-6">Schedule AI Intake Meeting</h2>
                <div className="space-y-6">
                  {/* Hiring Manager */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Select Hiring Manager *</label>
                    {isLoadingManagers ? (
                      <div className="flex items-center gap-2 text-gray-500 text-sm"><div className="animate-spin rounded-full h-4 w-4 border-2 border-brand-blue border-t-transparent"></div>Loading hiring managers...</div>
                    ) : hiringManagers.length === 0 ? (
                      <div className="text-red-600 text-sm bg-red-50 p-3 rounded-xl border border-red-200">No hiring managers found. Give someone the "Create jobs" permission in User Management first.</div>
                    ) : (
                      <select value={selectedHiringManager} onChange={(e) => {
                          setSelectedHiringManager(e.target.value);
                          const hm = hiringManagers.find(h => String(h.id) === e.target.value);
                          setSelectedHmTimezone(hm?.timezone || 'America/New_York');
                        }}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-brand-blue transition-all">
                        <option value="">-- Select Hiring Manager --</option>
                        {hiringManagers.map((hm) => (
                          <option key={hm.id} value={hm.id}>{hm.full_name} ({hm.email})</option>
                        ))}
                      </select>
                    )}
                    {selectedHmTimezone && (
                      <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                        <div className="flex items-center gap-2">
                          <span className="text-blue-600 text-lg">🌐</span>
                          <div>
                            <p className="text-sm font-semibold text-blue-800">
                              Hiring Manager's Timezone: {selectedHmTimezone.replace(/_/g, ' ')}
                            </p>
                            <p className="text-xs text-blue-600">
                              Their current time: {new Date().toLocaleTimeString('en-US', { timeZone: selectedHmTimezone, hour: '2-digit', minute: '2-digit', hour12: true, timeZoneName: 'short' })}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Meeting Title */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Meeting Title / Role *</label>
                    <input type="text" value={meetingTitle} onChange={(e) => setMeetingTitle(e.target.value)}
                      placeholder="e.g., Senior Backend Engineer Intake Call"
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-brand-blue transition-all" />
                  </div>

                  {/* Date and Time Row */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Date *</label>
                      <input type="date" value={meetingDate} onChange={(e) => setMeetingDate(e.target.value)} min={today}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-brand-blue transition-all" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Time {selectedHmTimezone ? <span className="text-blue-600 font-normal">({selectedHmTimezone.split('/').pop().replace(/_/g, ' ')})</span> : ''} *
                      </label>
                      <input type="time" value={meetingTime} onChange={(e) => setMeetingTime(e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-brand-blue transition-all" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Duration</label>
                      <select value={duration} onChange={(e) => setDuration(Number(e.target.value))}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-brand-blue transition-all">
                        <option value={15}>15 minutes</option>
                        <option value={30}>30 minutes</option>
                        <option value={45}>45 minutes</option>
                        <option value={60}>60 minutes</option>
                      </select>
                    </div>
                  </div>

                  {/* Time Preview - shows what HM sees vs what you see */}
                  {selectedHmTimezone && meetingDate && meetingTime && (
                    <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4">
                      <p className="text-sm font-bold text-amber-800 mb-2">⏰ Meeting Time Preview</p>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-white rounded-lg p-3 border border-amber-100">
                          <p className="text-xs text-gray-500 font-semibold uppercase mb-1">Hiring Manager sees</p>
                          <p className="text-lg font-bold text-gray-900">
                            {(() => {
                              try {
                                const [,] = meetingTime.split(':'); // h, m not needed — only d used
                                const d = new Date(`${meetingDate}T${meetingTime}:00`);
                                return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
                              } catch { return meetingTime; }
                            })()}
                          </p>
                          <p className="text-xs text-blue-600 font-medium">{selectedHmTimezone.split('/').pop().replace(/_/g, ' ')}</p>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-amber-100">
                          <p className="text-xs text-gray-500 font-semibold uppercase mb-1">You will see</p>
                          <p className="text-lg font-bold text-gray-900">
                            {(() => {
                              try {
                                const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
                                // Create date in HM's timezone, then show in user's local timezone
                                const hmDateTime = new Date(`${meetingDate}T${meetingTime}:00`);
                                // Get HM timezone offset
                                const hmStr = hmDateTime.toLocaleString('en-US', { timeZone: selectedHmTimezone });
                                const localStr = hmDateTime.toLocaleString('en-US', { timeZone: localTz });
                                const hmDate = new Date(hmStr);
                                const localDate = new Date(localStr);
                                const diffMs = localDate - hmDate;
                                const resultDate = new Date(hmDateTime.getTime() + diffMs);
                                return resultDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
                              } catch { return '—'; }
                            })()}
                          </p>
                          <p className="text-xs text-green-600 font-medium">{Intl.DateTimeFormat().resolvedOptions().timeZone.split('/').pop().replace(/_/g, ' ')}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* JD Upload for Meeting */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Upload Job Description *</label>
                    <input type="file" id="meeting-jd-upload" accept=".pdf,.doc,.docx,.txt" onChange={handleMeetingJdFileChange} className="hidden" />
                    <label htmlFor="meeting-jd-upload"
                      className={`border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer block hover:shadow-md ${
                        meetingJdFile ? 'border-brand-green bg-green-50' : 'border-gray-300 hover:border-brand-blue hover:bg-blue-50'
                      }`}>
                      <span className="text-3xl">📄</span>
                      <p className="font-semibold text-gray-900 mt-2">Upload Job Description</p>
                      <p className="text-xs text-gray-500 mt-1">PDF, DOC, DOCX, or TXT</p>
                      {meetingJdFile && <p className="text-sm text-brand-green font-bold mt-3">✓ {meetingJdFile.name}</p>}
                    </label>
                  </div>

                  {/* Info Box */}
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <p className="text-sm text-blue-800 font-semibold mb-1">How it works:</p>
                    <p className="text-sm text-blue-700">
                      Enter the date and time in the <strong>Hiring Manager's timezone</strong>. The hiring manager can join 5-10 minutes before/after the scheduled time. After the meeting, AI will process the conversation and generate recruiter documents and screening questions.
                    </p>
                  </div>

                  {/* Schedule Button */}
                  <button onClick={() => handleScheduleMeeting(false)} disabled={isScheduling || hiringManagers.length === 0}
                    className="w-full bg-gradient-to-r from-brand-blue to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold py-4 rounded-xl transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed text-lg">
                    {isScheduling ? (<span className="flex items-center justify-center gap-3"><div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>Scheduling...</span>) : 'Schedule Meeting →'}
                  </button>
                </div>
              </div>

              {/* Feature Cards */}
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 hover:shadow-xl transition-shadow">
                  <div className="text-4xl mb-4">🤖</div>
                  <h3 className="font-bold text-gray-900 mb-2">AI-Conducted Calls</h3>
                  <p className="text-sm text-gray-600">AI conducts professional intake calls with hiring managers</p>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 hover:shadow-xl transition-shadow">
                  <div className="text-4xl mb-4">📄</div>
                  <h3 className="font-bold text-gray-900 mb-2">Auto Documentation</h3>
                  <p className="text-sm text-gray-600">Generates recruiter-friendly documents automatically</p>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 hover:shadow-xl transition-shadow">
                  <div className="text-4xl mb-4">✅</div>
                  <h3 className="font-bold text-gray-900 mb-2">Screening Questions</h3>
                  <p className="text-sm text-gray-600">Creates tailored screening questions for candidates</p>
                </div>
              </div>
            </div>
          )}

          {/* ========== ENTERPRISE (Phase 3 Schedule Interview) ========== */}
          {activeMenu === 'enterprise' && isPlanAccessible('enterprise') && (
  <div className="space-y-6">
    <div>
      <h1 className="text-2xl font-bold text-gray-900">Enterprise Plan</h1>
      <p className="text-sm text-gray-600 mt-0.5">AI-powered technical interviews</p>
    </div>

      {/* Schedule Interview */}
      <div>

    {/* ═══ WIZARD PROGRESS BAR ═══ */}
    <div className="mb-6">
      <div className="flex items-center justify-between">
        {[
          { n: 1, label: 'Job & Hiring Manager', icon: '📋' },
          { n: 2, label: 'Candidate Info', icon: '👤' },
          { n: 3, label: 'Interview Config', icon: '⚙️' },
          { n: 4, label: 'Schedule & Review', icon: '📅' },
        ].map((s, i) => (
          <div key={s.n} className="flex items-center flex-1">
            <div className="flex flex-col items-center">
              <div className={`w-11 h-11 rounded-full flex items-center justify-center font-bold text-sm border-2 transition-all ${
                wizardStep > s.n ? 'bg-purple-600 border-purple-600 text-white shadow-md' :
                wizardStep === s.n ? 'bg-white border-purple-600 text-purple-700 shadow-md ring-4 ring-purple-100' :
                'bg-white border-gray-300 text-gray-400'
              }`}>
                {wizardStep > s.n ? '✓' : s.icon}
              </div>
              <span className={`text-xs mt-1.5 font-semibold whitespace-nowrap ${
                wizardStep === s.n ? 'text-purple-700' : wizardStep > s.n ? 'text-purple-400' : 'text-gray-400'
              }`}>{s.label}</span>
            </div>
            {i < 3 && (
              <div className={`flex-1 h-1 mx-3 mb-5 rounded-full transition-all ${wizardStep > s.n ? 'bg-purple-500' : 'bg-gray-200'}`} />
            )}
          </div>
        ))}
      </div>
    </div>

    {phase3Success && (
      <div className="bg-green-50 border-2 border-green-400 rounded-2xl p-5 flex items-center gap-3">
        <span className="text-2xl">✅</span>
        <p className="text-green-700 font-bold">{phase3Success}</p>
      </div>
    )}
    {phase3Error && (
      <div className="bg-red-50 border-2 border-red-400 rounded-2xl p-5 flex items-center gap-3">
        <span className="text-2xl">❌</span>
        <p className="text-red-700 font-bold">{phase3Error}</p>
      </div>
    )}

    {/* ═══════════ WIZARD STEP 1: JOB & HIRING MANAGER ═══════════ */}
    {wizardStep === 1 && (
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 space-y-6">
        <div className="border-b border-gray-100 pb-4">
          <h2 className="text-lg font-bold text-gray-900">Step 1 — Job & Hiring Manager</h2>
          <p className="text-sm text-gray-500 mt-0.5">Select the job requirement — hiring manager details will auto-fill</p>
        </div>

        <div>
          {/* Step 1: Select Requirement */}
          <label className="block text-sm font-semibold text-gray-700 mb-2">Select Job Requirement *</label>
          {isLoadingJDs ? (
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-brand-blue border-t-transparent"></div>
              Loading requirements...
            </div>
          ) : (() => {
            const completedJds = (jdByUser || []).flatMap(group =>
              (group.jds || []).filter(jd => jd.status === 'completed').map(jd => ({ ...jd, userName: group.userName }))
            );
            if (completedJds.length === 0) return (
              <div className="bg-amber-50 border border-amber-300 rounded-xl p-4">
                <p className="text-amber-800 font-semibold text-sm mb-1">⚠️ No completed requirements found</p>
                <p className="text-amber-700 text-sm">Only requirements that have been fully processed through Phase 1 (Essential) or Phase 2 (Professional in-take call) appear here. Please complete a requirement first before scheduling an AI interview.</p>
              </div>
            );
            return (
            <>
            <div className="relative">
              <input
                type="text"
                value={reqSearch}
                onChange={e => { setReqSearch(e.target.value); setReqDropdownOpen(true); }}
                onFocus={() => setReqDropdownOpen(true)}
                onBlur={() => setTimeout(() => setReqDropdownOpen(false), 200)}
                placeholder={selectedRequirement
                  ? completedJds.find(jd => String(jd.id) === selectedRequirement)
                    ? `${completedJds.find(jd => String(jd.id) === selectedRequirement).req_id || 'REQ-' + String(completedJds.find(jd => String(jd.id) === selectedRequirement).id).padStart(5,'0')} — ${completedJds.find(jd => String(jd.id) === selectedRequirement).jobTitle || ''}`
                    : '-- Select a requirement --'
                  : '🔍  Type to search or select a requirement...'}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-brand-blue transition-all bg-white"
              />
              {selectedRequirement && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedRequirement(''); clearJobTechScreen();
                    setSelectedRequirementTitle('');
                    setReqSearch('');
                    setHmName('');
                    setHmEmail('');
                    setRoleType('');
                    setSelectedTechStacks([]);
                  }}
                  className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600 text-sm"
                >✕</button>
              )}
              {reqDropdownOpen && completedJds && (
                <div className="absolute z-50 w-full mt-1 bg-white border-2 border-gray-200 rounded-xl shadow-xl max-h-64 overflow-y-auto">
                  {completedJds
                    .filter(jd => {
                      const q = reqSearch.toLowerCase();
                      if (!q) return true;
                      return (
                        (jd.req_id || '').toLowerCase().includes(q) ||
                        (jd.jobTitle || '').toLowerCase().includes(q) ||
                        (jd.userName || '').toLowerCase().includes(q)
                      );
                    })
                    .map(jd => (
                      <div
                        key={jd.id}
                        onMouseDown={() => pickRequirement(jd)}
                        className={`px-4 py-3 cursor-pointer hover:bg-purple-50 border-b border-gray-50 last:border-0 transition-colors ${String(jd.id) === selectedRequirement ? 'bg-purple-50' : ''}`}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-xs font-bold text-purple-600 bg-purple-50 px-2 py-0.5 rounded-md font-mono whitespace-nowrap">
                            {jd.req_id || `REQ-${String(jd.id).padStart(5,'0')}`}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate">{jd.jobTitle}</p>
                            {jd.userName && <p className="text-xs text-gray-400">{jd.userName}</p>}
                          </div>
                          <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded font-medium whitespace-nowrap ${
                            jd.source === 'ai_meeting' ? 'bg-blue-50 text-blue-600' : 'bg-green-50 text-green-600'
                          }`}>
                            {jd.source === 'ai_meeting' ? 'Phase 2' : 'Phase 1'}
                          </span>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-2">
              Only showing requirements processed in Phase 1 (Essential) or Phase 2 (Professional intake call).
            </p>
            </>
            );
          })()}
        </div>

        {/* Step 2b: Hiring Manager (auto-populated from requirement) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Hiring Manager Name
              {hmName && <span className="ml-2 text-xs font-normal text-green-600">✓ From requirement</span>}
            </label>
            <input
              type="text"
              value={hmName}
              onChange={(e) => setHmName(e.target.value)}
              placeholder="Auto-filled from requirement or enter manually"
              className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-brand-blue transition-all ${
                hmName ? 'border-green-300 bg-green-50' : 'border-gray-300'
              }`}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Hiring Manager Email</label>
            <input
              type="email"
              value={hmEmail}
              onChange={(e) => setHmEmail(e.target.value)}
              placeholder="hiring.manager@company.com"
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-brand-blue transition-all"
            />
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <button
            onClick={() => {
              if (!selectedRequirement) { setPhase3Error('Please select a job requirement'); return; }
              setPhase3Error('');
              setWizardStep(2);
            }}
            className="px-8 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-bold rounded-xl transition-all shadow-md hover:shadow-lg"
          >
            Next: Candidate Details →
          </button>
        </div>
      </div>
    )}

    {/* ═══════════ WIZARD STEP 2: CANDIDATE DETAILS ═══════════ */}
    {wizardStep === 2 && (
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 space-y-6">
        <div className="border-b border-gray-100 pb-4">
          <h2 className="text-lg font-bold text-gray-900">Step 2 — Candidate Info</h2>
          <p className="text-sm text-gray-500 mt-0.5">Enter basic candidate information and upload their resume</p>
        </div>

        {/* Step 2: Candidate Name & Email */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Candidate Name *</label>
            <input
              type="text"
              value={candidateName}
              onChange={(e) => setCandidateName(e.target.value)}
              placeholder="e.g., John Doe"
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-brand-blue transition-all"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Email *</label>
            <input
              type="email"
              value={candidateEmail}
              onChange={(e) => setCandidateEmail(e.target.value)}
              placeholder="john@example.com"
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-brand-blue transition-all"
            />
          </div>
        </div>


        {/* Resume Upload */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Resume (PDF or DOCX only) *</label>
          <input
            id="phase3-resume"
            type="file"
            accept=".pdf,.docx"
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) {
                const ext = file.name.split('.').pop().toLowerCase();
                if (['pdf', 'docx'].includes(ext)) {
                  setResume(file);
                  setPhase3Error('');
                } else {
                  setPhase3Error('Only PDF or DOCX files are accepted. Please upload a valid resume.');
                  e.target.value = ''; // clear the input
                }
              }
            }}
            className="hidden"
          />
          <label
            htmlFor="phase3-resume"
            className={`flex flex-col items-center justify-center w-full h-32 px-4 border-2 border-dashed rounded-xl cursor-pointer transition-all ${
              resume ? 'border-brand-green bg-green-50' : 'border-gray-300 hover:border-brand-blue hover:bg-blue-50'
            }`}
          >
            <span className="text-3xl mb-2">📄</span>
            <span className="text-sm text-gray-600 font-medium">
              {resume ? resume.name : 'Click to upload resume (PDF or DOCX only)'}
            </span>
          </label>
        </div>

        <div className="flex justify-between pt-2">
          <button
            onClick={() => { setPhase3Error(''); setWizardStep(1); }}
            className="px-6 py-3 border-2 border-gray-300 text-gray-600 font-bold rounded-xl hover:bg-gray-50 transition-all"
          >
            ← Back
          </button>
          <button
            onClick={async () => {
              if (!candidateName.trim()) { setPhase3Error('Please enter candidate name'); return; }
              if (!candidateEmail.trim()) { setPhase3Error('Please enter candidate email'); return; }
              if (!resume) { setPhase3Error('Please upload a resume'); return; }
              const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
              if (!emailRegex.test(candidateEmail)) { setPhase3Error('Please enter a valid email address'); return; }
              setPhase3Error('');
              const hasHistory = await handleCandidateHistoryCheck();
              if (!hasHistory) setWizardStep(3);
            }}
            className="px-8 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-bold rounded-xl transition-all shadow-md hover:shadow-lg"
          >
            Next: Interview Config →
          </button>
        </div>
      </div>
    )}

    {/* ═══════════ WIZARD STEP 3: SCHEDULE & REVIEW ═══════════ */}
    {/* ═══════════ STEP 3: INTERVIEW CONFIG ═══════════ */}
    {wizardStep === 3 && (
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 space-y-6">
        <div className="border-b border-gray-100 pb-4">
          <h2 className="text-lg font-bold text-gray-900">Step 3 — Interview Configuration</h2>
          <p className="text-sm text-gray-500 mt-0.5">Configure the role, tech stack, and interview settings</p>
        </div>

        {/* Role Type & Tech Stack */}
        <Phase3RoleTechSelector
          roleType={roleType}
          setRoleType={setRoleType}
          selectedTechStacks={selectedTechStacks}
          setSelectedTechStacks={setSelectedTechStacks}
          selectedRequirementTitle={selectedRequirementTitle}
          suggestedTechs={String(sourcesPreview?.requirement_id) === String(selectedRequirement) ? sourcesPreview.must_have_technologies : null}
          suggestionKey={selectedRequirement}
        />

        {/* Which JD + intake the questions come from (and warnings) */}
        <Phase3QuestionSourcesPreview requirementId={selectedRequirement} techStack={selectedTechStacks.join(', ')}
                                      onSources={setSourcesPreview} />

        {/* Duration, Coding Mode, Proctoring */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Interview Duration *</label>
            <select
              value={interviewDuration}
              onChange={(e) => setInterviewDuration(Number(e.target.value))}
              disabled={techScreenFromJob}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-brand-blue transition-all"
            >
              <option value={45}>45 minutes</option>
              <option value={60}>60 minutes (recommended)</option>
              <option value={75}>75 minutes</option>
            </select>
            {techScreenFromJob && <p className="text-xs text-gray-500 mt-1" data-testid="set-by-hm">Set by hiring manager</p>}
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Coding Mode *</label>
            <select
              value={codingMode}
              onChange={(e) => setCodingMode(e.target.value)}
              disabled={techScreenFromJob}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-brand-blue transition-all"
            >
              <option value="verbal_pseudocode">Verbal + Pseudocode</option>
              <option value="live_coding">Live Coding</option>
            </select>
            {techScreenFromJob && <p className="text-xs text-gray-500 mt-1" data-testid="set-by-hm">Set by hiring manager</p>}
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Proctoring Strictness *</label>
            <select
              value={proctoringStrictness}
              onChange={(e) => setProctoringStrictness(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-brand-blue transition-all"
            >
              <option value="low">Low (minimal monitoring)</option>
              <option value="medium">Medium (recommended)</option>
              <option value="high">High (strict monitoring)</option>
            </select>
          </div>
        </div>

        {/* Back / Next */}
        <div className="flex justify-between pt-2">
          <button
            onClick={() => { setPhase3Error(''); setWizardStep(2); }}
            className="px-6 py-3 border-2 border-gray-300 text-gray-600 font-bold rounded-xl hover:bg-gray-50 transition-all"
          >
            ← Back
          </button>
          <button
            onClick={() => { setPhase3Error(''); setWizardStep(4); }}
            className="px-8 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-bold rounded-xl transition-all shadow-md hover:shadow-lg"
          >
            Next: Schedule & Review →
          </button>
        </div>
      </div>
    )}

    {wizardStep === 4 && (
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 space-y-6">
        <div className="border-b border-gray-100 pb-4">
          <h2 className="text-lg font-bold text-gray-900">Step 4 — Schedule & Review</h2>
          <p className="text-sm text-gray-500 mt-0.5">Set the interview date and time, then confirm all details</p>
        </div>

        {/* Step 5: Candidate Timezone, Date and Time */}
        {/* Timezone selector - MUST come before date/time */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            🌐 Candidate's Timezone *
            <span className="ml-2 text-xs font-normal text-gray-500">The date and time below are in this timezone</span>
          </label>
          <select
            value={candidateTimezone}
            onChange={(e) => setCandidateTimezone(e.target.value)}
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-brand-blue transition-all bg-white"
          >
            <optgroup label="🇺🇸 United States">
              <option value="America/New_York">Eastern Time (ET) — New York, Miami, Atlanta</option>
              <option value="America/Chicago">Central Time (CT) — Chicago, Dallas, Houston</option>
              <option value="America/Denver">Mountain Time (MT) — Denver, Salt Lake City</option>
              <option value="America/Phoenix">Mountain Time (no DST) — Phoenix, Arizona</option>
              <option value="America/Los_Angeles">Pacific Time (PT) — Los Angeles, Seattle, SF</option>
              <option value="America/Anchorage">Alaska Time (AKT) — Anchorage</option>
              <option value="Pacific/Honolulu">Hawaii Time (HST) — Honolulu</option>
            </optgroup>
            <optgroup label="🇨🇦 Canada">
              <option value="America/Toronto">Eastern Time — Toronto, Ottawa</option>
              <option value="America/Vancouver">Pacific Time — Vancouver</option>
              <option value="America/Edmonton">Mountain Time — Calgary, Edmonton</option>
              <option value="America/Halifax">Atlantic Time — Halifax</option>
            </optgroup>
            <optgroup label="🇬🇧 🇮🇪 UK & Ireland">
              <option value="Europe/London">GMT / BST — London, Dublin</option>
            </optgroup>
            <optgroup label="🇪🇺 Europe">
              <option value="Europe/Paris">Central European Time — Paris, Berlin, Rome, Madrid</option>
              <option value="Europe/Amsterdam">Central European Time — Amsterdam, Brussels</option>
              <option value="Europe/Stockholm">Central European Time — Stockholm, Oslo, Copenhagen</option>
              <option value="Europe/Warsaw">Central European Time — Warsaw, Prague, Vienna</option>
              <option value="Europe/Helsinki">Eastern European Time — Helsinki, Tallinn, Riga</option>
              <option value="Europe/Bucharest">Eastern European Time — Bucharest, Sofia</option>
              <option value="Europe/Athens">Eastern European Time — Athens</option>
              <option value="Europe/Moscow">Moscow Time (MSK) — Moscow, St. Petersburg</option>
            </optgroup>
            <optgroup label="🌍 Middle East & Africa">
              <option value="Asia/Dubai">Gulf Standard Time — Dubai, Abu Dhabi</option>
              <option value="Asia/Riyadh">Arabia Standard Time — Riyadh, Kuwait</option>
              <option value="Asia/Jerusalem">Israel Time — Tel Aviv, Jerusalem</option>
              <option value="Africa/Cairo">Egypt Standard Time — Cairo</option>
              <option value="Africa/Johannesburg">South Africa Time — Johannesburg, Cape Town</option>
              <option value="Africa/Lagos">West Africa Time — Lagos, Accra</option>
              <option value="Africa/Nairobi">East Africa Time — Nairobi</option>
            </optgroup>
            <optgroup label="🇮🇳 South Asia">
              <option value="Asia/Kolkata">India Standard Time (IST) — Mumbai, Bangalore, Delhi</option>
              <option value="Asia/Karachi">Pakistan Standard Time — Karachi, Lahore, Islamabad</option>
              <option value="Asia/Dhaka">Bangladesh Standard Time — Dhaka</option>
              <option value="Asia/Colombo">Sri Lanka Time — Colombo</option>
              <option value="Asia/Kathmandu">Nepal Time — Kathmandu</option>
            </optgroup>
            <optgroup label="🌏 East & Southeast Asia">
              <option value="Asia/Shanghai">China Standard Time (CST) — Beijing, Shanghai, Shenzhen</option>
              <option value="Asia/Hong_Kong">Hong Kong Time — Hong Kong</option>
              <option value="Asia/Taipei">Taiwan Time — Taipei</option>
              <option value="Asia/Tokyo">Japan Standard Time (JST) — Tokyo, Osaka</option>
              <option value="Asia/Seoul">Korea Standard Time — Seoul</option>
              <option value="Asia/Singapore">Singapore Time (SGT) — Singapore</option>
              <option value="Asia/Kuala_Lumpur">Malaysia Time — Kuala Lumpur</option>
              <option value="Asia/Bangkok">Indochina Time — Bangkok, Ho Chi Minh City</option>
              <option value="Asia/Manila">Philippines Time — Manila</option>
              <option value="Asia/Jakarta">Western Indonesia Time — Jakarta</option>
            </optgroup>
            <optgroup label="🇦🇺 Australia & Pacific">
              <option value="Australia/Sydney">AEST / AEDT — Sydney, Melbourne, Brisbane</option>
              <option value="Australia/Adelaide">ACST / ACDT — Adelaide</option>
              <option value="Australia/Perth">AWST — Perth</option>
              <option value="Pacific/Auckland">New Zealand Time — Auckland, Wellington</option>
            </optgroup>
            <optgroup label="🌎 Latin America">
              <option value="America/Sao_Paulo">Brazil Time — São Paulo, Rio de Janeiro</option>
              <option value="America/Argentina/Buenos_Aires">Argentina Time — Buenos Aires</option>
              <option value="America/Mexico_City">Mexico City Time — Mexico City, Guadalajara</option>
              <option value="America/Bogota">Colombia Time — Bogotá, Medellín</option>
              <option value="America/Santiago">Chile Time — Santiago</option>
              <option value="America/Lima">Peru Time — Lima</option>
            </optgroup>
            <optgroup label="🌐 UTC">
              <option value="UTC">UTC — Coordinated Universal Time</option>
            </optgroup>
          </select>
          {/* Live timezone preview */}
          {candidateTimezone && (
            <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
              <span>🕐 Current time in candidate's timezone:</span>
              <span className="font-semibold text-gray-700">
                {new Date().toLocaleTimeString('en-US', {
                  timeZone: candidateTimezone,
                  hour: '2-digit', minute: '2-digit', hour12: true
                })}
                {' '}
                ({new Intl.DateTimeFormat('en-US', { timeZone: candidateTimezone, timeZoneName: 'short' })
                  .formatToParts(new Date()).find(p => p.type === 'timeZoneName')?.value})
              </span>
            </div>
          )}
        </div>

        {/* Date and Time — in the selected candidate timezone */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Interview Date *</label>
            <input
              type="date"
              value={interviewDate}
              onChange={(e) => setInterviewDate(e.target.value)}
              min={today}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-brand-blue transition-all"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Interview Time *</label>
            <input
              type="time"
              value={interviewTime}
              onChange={(e) => setInterviewTime(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-brand-blue transition-all"
            />
          </div>
        </div>
        {/* Confirmation preview when both date and time are selected */}
        {interviewDate && interviewTime && candidateTimezone && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 flex items-start gap-2">
            <span className="text-indigo-500 text-lg mt-0.5">📅</span>
            <div>
              <p className="text-sm font-semibold text-indigo-800">
                Candidate will be invited to join at:
              </p>
              <p className="text-sm text-indigo-700 mt-0.5">
                {new Date(`${interviewDate}T${interviewTime}`).toLocaleDateString('en-US', {
                  weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                })}
                {' at '}
                {new Date(`${interviewDate}T${interviewTime}`).toLocaleTimeString('en-US', {
                  hour: '2-digit', minute: '2-digit', hour12: true
                })}
                {' '}
                <span className="font-bold">
                  ({new Intl.DateTimeFormat('en-US', { timeZone: candidateTimezone, timeZoneName: 'short' })
                    .formatToParts(new Date()).find(p => p.type === 'timeZoneName')?.value})
                </span>
              </p>
              <p className="text-xs text-indigo-500 mt-1">Stored as UTC. Email invite will show this time in the candidate's timezone.</p>
            </div>
          </div>
        )}

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <p className="text-sm text-blue-800 font-semibold mb-1">How it works:</p>
          <p className="text-sm text-blue-700">
            Candidate receives secure email with interview link. ElevenLabs AI conducts technical interview with questions tailored to the requirement. Interview is recorded and scored by Claude AI. Results delivered within 5 minutes.
          </p>
        </div>

        {/* Schedule Button */}
        <button
          onClick={handleSchedulePhase3}
          disabled={isSchedulingPhase3 || (jdByUser && jdByUser.length === 0)}
          className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-bold py-4 rounded-xl transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed text-lg"
        >
          {isSchedulingPhase3 ? (
            <span className="flex items-center justify-center gap-3">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
              Scheduling...
            </span>
          ) : (
            '✓ Schedule Interview →'
          )}
        </button>

        {/* Review Summary */}
        {interviewDate && interviewTime && (
          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6">
            <h3 className="text-sm font-bold text-gray-700 uppercase tracking-widest mb-4">📋 Review Before Scheduling</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 text-sm">
              <div className="flex justify-between border-b border-gray-100 pb-2">
                <span className="text-gray-500 font-medium">Job Requirement</span>
                <span className="text-gray-900 font-semibold text-right max-w-[60%] truncate">{selectedRequirementTitle || 'Selected'}</span>
              </div>
              <div className="flex justify-between border-b border-gray-100 pb-2">
                <span className="text-gray-500 font-medium">Hiring Manager</span>
                <span className="text-gray-900 font-semibold">{hmName || '—'}</span>
              </div>
              <div className="flex justify-between border-b border-gray-100 pb-2">
                <span className="text-gray-500 font-medium">Candidate</span>
                <span className="text-gray-900 font-semibold">{candidateName}</span>
              </div>
              <div className="flex justify-between border-b border-gray-100 pb-2">
                <span className="text-gray-500 font-medium">Email</span>
                <span className="text-gray-900 font-semibold text-right max-w-[60%] truncate">{candidateEmail}</span>
              </div>
              <div className="flex justify-between border-b border-gray-100 pb-2">
                <span className="text-gray-500 font-medium">Level</span>
                <span className="text-gray-900 font-semibold">Set from the role (JD + intake)</span>
              </div>
              <div className="flex justify-between border-b border-gray-100 pb-2">
                <span className="text-gray-500 font-medium">Role</span>
                <span className="text-gray-900 font-semibold">{roleType || '—'}</span>
              </div>
              <div className="flex justify-between border-b border-gray-100 pb-2">
                <span className="text-gray-500 font-medium">Duration</span>
                <span className="text-gray-900 font-semibold">{interviewDuration} min · <span className="capitalize">{codingMode?.replace('_', ' ')}</span></span>
              </div>
              <div className="flex justify-between border-b border-gray-100 pb-2">
                <span className="text-gray-500 font-medium">Resume</span>
                <span className="text-gray-900 font-semibold">{resume?.name || '—'}</span>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-between pt-2">
          <button
            onClick={() => { setPhase3Error(''); setWizardStep(3); }}
            className="px-6 py-3 border-2 border-gray-300 text-gray-600 font-bold rounded-xl hover:bg-gray-50 transition-all"
          >
            ← Back
          </button>
          <button
            onClick={handleSchedulePhase3}
            disabled={isSchedulingPhase3 || (jdByUser && jdByUser.length === 0)}
            className="px-8 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-bold rounded-xl transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed text-lg"
          >
            {isSchedulingPhase3 ? (
              <span className="flex items-center gap-3">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                Scheduling...
              </span>
            ) : (
              '✓ Schedule Interview'
            )}
          </button>
        </div>
      </div>
    )}


    {/* Feature Cards */}
    <div className="grid md:grid-cols-3 gap-6">
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 hover:shadow-xl transition-shadow">
        <div className="text-4xl mb-4">🤖</div>
        <h3 className="font-bold text-gray-900 mb-2">AI-Conducted Interviews</h3>
        <p className="text-sm text-gray-600">ElevenLabs AI conducts real technical interviews with adaptive questions</p>
      </div>
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 hover:shadow-xl transition-shadow">
        <div className="text-4xl mb-4">⭐</div>
        <h3 className="font-bold text-gray-900 mb-2">Smart Scoring</h3>
        <p className="text-sm text-gray-600">Claude AI scores across 5 dimensions with percentile rankings vs cohort</p>
      </div>
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 hover:shadow-xl transition-shadow">
        <div className="text-4xl mb-4">📊</div>
        <h3 className="font-bold text-gray-900 mb-2">Detailed Reports</h3>
        <p className="text-sm text-gray-600">Hiring manager-friendly reports with candidate feedback and recommendations</p>
      </div>
    </div>
      </div>
  </div>
)}
        </div>
      </main>

      {/* Audio Format Error Modal */}
      {audioFormatError && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-6" onClick={() => setAudioFormatError(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/></svg>
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">Audio Format Not Supported</h3>
                  <p className="text-sm text-gray-500 mt-0.5 truncate max-w-[280px]">{audioFormatError.filename}</p>
                </div>
              </div>
              <p className="text-sm text-gray-700 mb-4">
                <span className="font-semibold text-red-600">.{audioFormatError.format}</span> files are not accepted by Azure Speech Services and cannot be processed.
              </p>
              <div className="bg-gray-50 rounded-xl p-4 mb-5">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Accepted Formats</p>
                <div className="flex flex-wrap gap-2">
                  {['MP3', 'WAV', 'OGG', 'FLAC', 'WebM'].map(f => (
                    <span key={f} className="px-3 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full">{f}</span>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-3">💡 If you have an M4A or MP4, convert it to MP3 using any free online converter before uploading.</p>
              </div>
              <button onClick={() => setAudioFormatError(null)} className="w-full py-2.5 bg-gray-900 hover:bg-gray-700 text-white text-sm font-bold rounded-xl transition-colors">Got it</button>
            </div>
          </div>
        </div>
      )}

      {/* Candidate History Modal */}
      {showHistoryModal && candidateHistory && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
            <div className="px-6 py-5 border-b border-gray-100 flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-xl">⚠️</span>
                  <h2 className="text-base font-bold text-gray-900">Candidate Has Previous Interviews</h2>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  <span className="font-semibold">{candidateName}</span> ({candidateEmail}) has taken{' '}
                  <span className="font-semibold text-purple-700">{candidateHistory.length}</span>{' '}
                  interview{candidateHistory.length !== 1 ? 's' : ''} at your company before.
                </p>
              </div>
              <button onClick={() => setShowHistoryModal(false)} className="text-gray-400 hover:text-gray-600 text-xl ml-4">✕</button>
            </div>
            <div className="overflow-y-auto flex-1">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                  <tr>
                    {['Req ID','Job Title','Date','Days Ago','Status','Score','View'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {candidateHistory.map((iv, i) => {
                    const stColors = { completed:'bg-green-100 text-green-700', processed:'bg-blue-100 text-blue-700', scheduled:'bg-purple-100 text-purple-700', in_progress:'bg-yellow-100 text-yellow-700', ready:'bg-indigo-100 text-indigo-700' };
                    const recColors = { exceeds_bar:'text-green-600', meets_bar:'text-blue-600', borderline:'text-yellow-600', below_bar:'text-red-600' };
                    const st = (iv.status||'').toLowerCase();
                    const rec = (iv.recommendation||'').toLowerCase();
                    return (
                      <tr key={iv.interview_id} className={`border-b border-gray-50 hover:bg-gray-50 ${i%2===1?'bg-gray-50/40':''}`}>
                        <td className="px-4 py-3"><span className="text-xs font-mono font-bold text-purple-600 bg-purple-50 px-2 py-0.5 rounded">{iv.req_id||`REQ-${iv.requirement_id}`}</span></td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-800 max-w-[140px] truncate">{iv.job_title||'—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{iv.scheduled_at ? new Date(iv.scheduled_at).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) : '—'}</td>
                        <td className="px-4 py-3">
                          {iv.days_ago !== null ? (
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${iv.days_ago<30?'bg-red-50 text-red-600':iv.days_ago<90?'bg-yellow-50 text-yellow-600':'bg-gray-100 text-gray-500'}`}>
                              {iv.days_ago===0?'Today':`${iv.days_ago}d ago`}
                            </span>
                          ) : '—'}
                        </td>
                        <td className="px-4 py-3"><span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${stColors[st]||'bg-gray-100 text-gray-600'}`}>{iv.status}</span></td>
                        <td className="px-4 py-3 text-sm">{iv.overall_score ? <span className={`font-bold ${recColors[rec]||'text-gray-700'}`}>{Math.round(iv.overall_score)}/400</span> : '—'}</td>
                        <td className="px-4 py-3 text-center">
                          <button onClick={() => { setShowHistoryModal(false); navigate(`/phase3/interview/${iv.interview_id}`); }}
                            className="p-1.5 rounded-lg hover:bg-purple-100 text-purple-600 transition-colors" title="View interview result">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0zM2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                            </svg>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl flex items-center justify-between">
              <p className="text-xs text-gray-400">A candidate can take multiple interviews. Proceed if this is intentional.</p>
              <div className="flex gap-3">
                <button onClick={() => setShowHistoryModal(false)} className="px-4 py-2 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm font-semibold rounded-xl">Cancel</button>
                <button onClick={() => { setShowHistoryModal(false); setWizardStep(3); }} className="px-5 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-bold rounded-xl">Proceed with New Interview</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========== PLANS MODAL ========== */}
      {showPlansModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-6" onClick={() => setShowPlansModal(false)}>
          <div className="bg-white rounded-3xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-brand-blue to-blue-600 p-8 rounded-t-3xl">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-3xl font-bold text-white">Subscription Plans</h2>
                  <p className="text-blue-100 mt-2 font-medium">Choose the right plan for your recruitment needs</p>
                </div>
                <button onClick={() => setShowPlansModal(false)} className="text-white/80 hover:text-white text-3xl leading-none p-2 hover:bg-white/10 rounded-xl transition-colors">✕</button>
              </div>
            </div>
            <div className="p-8">
              <div className="grid md:grid-cols-3 gap-6">
                {plansData.map((plan) => {
                  const isCurrent = subscriptionPlan === plan.key;
                  const isAccessible = isPlanAccessible(plan.key);
                  return (
                    <div key={plan.key} className={`relative rounded-2xl border-2 overflow-hidden transition-all hover:shadow-xl ${
                      isCurrent ? `${plan.borderColor} shadow-lg ring-2 ring-offset-2 ${plan.key === 'enterprise' ? 'ring-purple-300' : plan.key === 'professional' ? 'ring-blue-300' : 'ring-green-300'}` : 'border-gray-200'
                    }`}>
                      {isCurrent && (<div className={`bg-gradient-to-r ${plan.gradient} text-white text-center py-2 text-sm font-bold uppercase tracking-wider`}>✓ Current Plan</div>)}
                      <div className="p-6">
                        <div className="text-center mb-4"><span className="text-4xl">{plan.icon}</span><h3 className="text-xl font-bold text-gray-900 mt-2">{plan.name}</h3></div>
                        <div className="text-center mb-4">
                          <div className="flex items-baseline justify-center gap-1"><span className="text-4xl font-black text-gray-900">${plan.price}</span><span className="text-gray-500 font-medium">/month</span></div>
                          <p className="text-sm text-gray-500 mt-1">{plan.limit}</p>
                        </div>
                        <ul className="space-y-3 mb-6">
                          {plan.features.map((feature, i) => (
                            <li key={i} className="flex items-start gap-2.5">
                              <svg className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isAccessible ? 'text-green-500' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                              </svg>
                              <span className={`text-sm ${isAccessible ? 'text-gray-700' : 'text-gray-400'}`}>{feature}</span>
                            </li>
                          ))}
                        </ul>
                        {isCurrent ? (
                          <div className={`w-full py-3 rounded-xl text-center text-sm font-bold bg-gradient-to-br ${plan.lightGradient} ${plan.textColor} border ${plan.borderColor}`}>Your Current Plan</div>
                        ) : isAccessible ? (
                          <div className="w-full py-3 rounded-xl text-center text-sm font-bold bg-green-50 text-green-700 border border-green-200">✓ Included in Your Plan</div>
                        ) : (
                          <button className={`w-full py-3 rounded-xl text-sm font-bold text-white bg-gradient-to-r ${plan.gradient} hover:shadow-lg transition-all transform hover:scale-105`}>
                            Upgrade to {plan.name} →
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-center text-sm text-gray-500 mt-6">Need help choosing? Contact us at <span className="font-semibold text-brand-blue">admin@uni5d.ai</span></p>

              {/* Manage Billing / Cancel */}
              <div className="mt-6 border-t border-gray-100 pt-6 flex flex-wrap items-center justify-between gap-3">
                <button
                  onClick={async () => {
                    const tok = localStorage.getItem('token');
                    const BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
                    try {
                      const res = await fetch(`${BASE}/api/stripe/billing-portal`, { headers: { 'Authorization': `Bearer ${tok}` } });
                      const data = await res.json();
                      if (data.portal_url) window.location.href = data.portal_url;
                    } catch { alert('Could not open billing portal. Please try again.'); }
                  }}
                  className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all"
                >
                  💳 Manage Billing & Payment
                </button>
                {subscriptionData?.status === 'cancelling' ? (
                  <button
                    onClick={async () => {
                      const tok = localStorage.getItem('token');
                      const BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
                      const res = await fetch(`${BASE}/api/stripe/reactivate`, { method: 'POST', headers: { 'Authorization': `Bearer ${tok}` } });
                      if (res.ok) { setSubscriptionData(prev => ({ ...prev, status: 'active' })); alert('Subscription reactivated!'); }
                    }}
                    className="text-sm text-green-600 font-semibold hover:underline"
                  >
                    ↺ Undo Cancellation
                  </button>
                ) : subscriptionData?.status === 'active' ? (
                  <button onClick={() => setShowCancelConfirm(true)} className="text-sm text-red-500 font-semibold hover:underline">
                    Cancel Subscription
                  </button>
                ) : null}
              </div>

              {/* Add-on Packs */}
              <div className="mt-6 border-t border-gray-100 pt-6">
                <h3 className="text-lg font-bold text-gray-900 mb-1">Need More Capacity?</h3>
                <p className="text-sm text-gray-500 mb-4">One-time packs · valid 30 days from purchase</p>
                <div className="space-y-4">
                  {/* Phase 1 add-ons — all plans */}
                  <div>
                    <p className="text-xs font-bold text-blue-700 uppercase tracking-wide mb-2">📄 JD Upload Packs — All Plans</p>
                    <div className="grid grid-cols-3 gap-2">
                      {[{key:'addon_p1_starter',label:'+5 uploads',price:19},{key:'addon_p1_growth',label:'+10 uploads',price:29},{key:'addon_p1_scale',label:'+20 uploads',price:49}].map(a => (
                        <button key={a.key} onClick={async () => {
                          const tok = localStorage.getItem('token');
                          const BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
                          const res = await fetch(`${BASE}/api/stripe/create-addon-session`, { method:'POST', headers:{'Authorization':`Bearer ${tok}`,'Content-Type':'application/json'}, body: JSON.stringify({addon_lookup_key: a.key}) });
                          const data = await res.json();
                          if (data.checkout_url) window.location.href = data.checkout_url;
                          else alert(data.detail || 'Error creating session');
                        }} className="border border-blue-200 hover:border-blue-400 rounded-xl p-3 text-center transition-all hover:shadow-sm">
                          <div className="text-xs text-blue-600 font-semibold">{a.label}</div>
                          <div className="text-lg font-black text-gray-900">${a.price}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                  {/* Phase 2 add-ons — Professional + Enterprise */}
                  {(subscriptionPlan === 'professional' || subscriptionPlan === 'enterprise') && (
                    <div>
                      <p className="text-xs font-bold text-green-700 uppercase tracking-wide mb-2">🤝 Intake Call Packs — Pro & Enterprise</p>
                      <div className="grid grid-cols-3 gap-2">
                        {[{key:'addon_p2_starter',label:'+5 calls',price:39},{key:'addon_p2_growth',label:'+10 calls',price:79},{key:'addon_p2_scale',label:'+20 calls',price:149}].map(a => (
                          <button key={a.key} onClick={async () => {
                            const tok = localStorage.getItem('token');
                            const BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
                            const res = await fetch(`${BASE}/api/stripe/create-addon-session`, { method:'POST', headers:{'Authorization':`Bearer ${tok}`,'Content-Type':'application/json'}, body: JSON.stringify({addon_lookup_key: a.key}) });
                            const data = await res.json();
                            if (data.checkout_url) window.location.href = data.checkout_url;
                            else alert(data.detail || 'Error creating session');
                          }} className="border border-green-200 hover:border-green-400 rounded-xl p-3 text-center transition-all hover:shadow-sm">
                            <div className="text-xs text-green-600 font-semibold">{a.label}</div>
                            <div className="text-lg font-black text-gray-900">${a.price}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Phase 3 add-ons — Enterprise only */}
                  {subscriptionPlan === 'enterprise' && (
                    <div>
                      <p className="text-xs font-bold text-purple-700 uppercase tracking-wide mb-2">🎯 Interview Packs — Enterprise Only</p>
                      <div className="grid grid-cols-3 gap-2">
                        {[{key:'addon_p3_starter',label:'+5 interviews',price:149},{key:'addon_p3_growth',label:'+10 interviews',price:249},{key:'addon_p3_scale',label:'+25 interviews',price:499}].map(a => (
                          <button key={a.key} onClick={async () => {
                            const tok = localStorage.getItem('token');
                            const BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
                            const res = await fetch(`${BASE}/api/stripe/create-addon-session`, { method:'POST', headers:{'Authorization':`Bearer ${tok}`,'Content-Type':'application/json'}, body: JSON.stringify({addon_lookup_key: a.key}) });
                            const data = await res.json();
                            if (data.checkout_url) window.location.href = data.checkout_url;
                            else alert(data.detail || 'Error creating session');
                          }} className="border border-purple-200 hover:border-purple-400 rounded-xl p-3 text-center transition-all hover:shadow-sm">
                            <div className="text-xs text-purple-600 font-semibold">{a.label}</div>
                            <div className="text-lg font-black text-gray-900">${a.price}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-6">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-7 h-7 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Cancel Subscription?</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Your subscription will remain active until the end of your current billing period.
                {subscriptionData?.current_period_end && (
                  <span className="font-semibold text-gray-700"> Access until: {new Date(subscriptionData.current_period_end).toLocaleDateString()}</span>
                )}
                <br/>No refunds will be issued for the current period.
              </p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowCancelConfirm(false)}
                className="flex-1 py-3 border-2 border-gray-200 rounded-xl text-gray-700 font-bold hover:bg-gray-50 transition-all">
                Keep Subscription
              </button>
              <button
                disabled={cancelLoading}
                onClick={async () => {
                  setCancelLoading(true);
                  const tok = localStorage.getItem('token');
                  const BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
                  try {
                    const res = await fetch(`${BASE}/api/stripe/cancel`, { method: 'POST', headers: { 'Authorization': `Bearer ${tok}` } });
                    const data = await res.json();
                    if (res.ok) { setSubscriptionData(prev => ({ ...prev, status: 'cancelling' })); setShowCancelConfirm(false); }
                    else alert(data.detail || 'Could not cancel. Please try again.');
                  } catch { alert('Network error. Please try again.'); }
                  setCancelLoading(false);
                }}
                className="flex-1 py-3 bg-red-500 hover:bg-red-600 text-white rounded-xl font-bold transition-all disabled:opacity-50">
                {cancelLoading ? 'Cancelling…' : 'Yes, Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate JD Warning Modal (Phase 1 + Phase 2) */}
      {duplicateWarning.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col">
            <div className="px-6 py-5 border-b border-gray-200 bg-amber-50">
              <div className="flex items-start gap-3">
                <span className="text-2xl">&#9888;&#65039;</span>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Duplicate JD Detected</h3>
                  <p className="text-sm text-gray-600 mt-0.5">
                    {duplicateWarning.phase === 'phase2'
                      ? 'This exact Job Description has already been processed for this hiring manager. Review the prior results below, or continue anyway to run it again.'
                      : 'This exact Job Description has already been processed. Review the prior results below, or continue anyway to run it again.'}
                  </p>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 overflow-y-auto flex-1">
              <div className="space-y-3">
                {duplicateWarning.duplicates.map((dup) => (
                  <div
                    key={dup.meeting_id || dup.id}
                    className="flex items-center justify-between gap-4 p-4 border border-gray-200 rounded-lg hover:border-brand-blue transition-all"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-gray-900 truncate">{dup.job_title || 'Untitled Role'}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${
                          dup.source === 'ai_meeting'
                            ? 'bg-teal-50 text-teal-700 border border-teal-200'
                            : 'bg-blue-50 text-blue-700 border border-blue-200'
                        }`}>
                          {dup.source === 'ai_meeting' ? 'Phase 2 · AI Call' : 'Phase 1 · JD Upload'}
                        </span>
                      </div>
                      <div className="text-sm text-gray-500">
                        {dup.hiring_manager_name && <span>HM: {dup.hiring_manager_name} · </span>}
                        {dup.created_at && <span>{new Date(dup.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => navigate(`/view-requirement/${dup.id}`)}
                      title="View result"
                      className="flex-shrink-0 p-2 text-gray-500 hover:text-brand-blue hover:bg-blue-50 rounded-lg transition-all"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3 bg-gray-50">
              <button
                type="button"
                onClick={() => setDuplicateWarning({ show: false, duplicates: [], phase: null })}
                className="px-5 py-2.5 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-all"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  const phase = duplicateWarning.phase;
                  setDuplicateWarning({ show: false, duplicates: [], phase: null });
                  if (phase === 'phase2') { handleScheduleMeeting(true); } else { handleProcessFiles(true); }
                }}
                className="px-5 py-2.5 bg-brand-blue text-white font-semibold rounded-lg hover:bg-blue-700 transition-all"
              >
                Continue Anyway &rarr;
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CompanyAdminDashboard;